<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <div class="bg-white border-b">
      <div class="mx-auto px-4 py-4">
        <div class="tw-flex items-center justify-between">
          <div class="tw-flex items-center gap-4">
            <ds-button
              variant="ghost"
              size="icon"
              icon="arrow-left"
              @click="goBack"
            />
            <div class="tw-flex items-center gap-2">
              <Icon name="map" class="h-6 w-6 text-primary"/>
              <h1 class="text-2xl font-bold">
                {{ isNew ? 'Create New Map' : (map && map.name) ? map.name : 'Edit Map' }}
              </h1>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mx-auto px-4 py-8">
      <div class="gap-4 tw-flex flex-row ">
        <!-- Configuration Panel -->
        <div class="space-y-4 w-1/3">
          <div class="bg-white rounded-lg shadow-sm border">
            <div class="p-6 border-b">
              <h2 class="text-xl font-semibold">Map Configuration</h2>
              <p class="text-gray-600 mt-1">
                Configure your map settings and spotting areas
              </p>
            </div>
            <div class="p-6 space-y-6">
              <!-- Map Name -->
              <div class="space-y-2">
                <label for="mapName" class="block text-sm font-medium text-gray-700">
                  Map Name <span class="text-red-500">*</span>
                </label>
                <ds-input
                  id="mapName"
                  v-model="mapName"
                  placeholder="Enter map name..."
                  :class="nameError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''"
                  @blur="validateName"
                  is-simplified
                />
                <p v-if="nameError" class="text-red-600 text-sm">{{ nameError }}</p>
              </div>

              <!-- API Key -->
              <div class="space-y-2">
                <label for="apiKey" class="block text-sm font-medium text-gray-700">
                  API Key
                </label>
                <div class="tw-flex gap-2">
                  <ds-input
                    id="apiKey"
                    v-model="apiKey"
                    :readonly="true"
                    class="font-mono text-sm"
                    is-simplified
                  />
                  <ds-button
                    variant="rounded-secondary"
                    size="small"
                    label="Copy"
                    @click="copyApiKey"
                  />
                </div>
              </div>

              <div class="tw-flex items-center gap-3">
                <input
                  id="showAddOrganisationButton"
                  type="checkbox"
                  v-model="mapConfig.showEcosystemLogo"
                  class="h-2 w-2 text-primary focus:ring-primary border-gray-300 rounded mb-2"
                />
                <label for="showAddOrganisationButton" class="text-sm text-gray-700">
                  Show the ecosystem logo and "By ..." text in the top right corner
                </label>
              </div>
              <p class="text-xs text-gray-500 ml-7 !mt-0">
                When enabled, in the top right of the map the ecosystem logo will be displayed.
              </p>

              <!-- Map Center & Zoom -->
              <div class="space-y-4">
                <div class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">
                    Map Center & Zoom Level
                  </label>
                  <div class="grid grid-cols-3 gap-2 text-sm">
                    <div>
                      <label class="text-xs text-gray-500">Zoom: {{ mapConfig.zoom }}</label>
                    </div>
                    <div>
                      <label class="text-xs text-gray-500">Lat: {{ parseFloat(mapConfig.lat).toFixed(6) }}</label>
                    </div>
                    <div>
                      <label class="text-xs text-gray-500">Lng: {{ parseFloat(mapConfig.lng).toFixed(6) }}</label>
                    </div>
                  </div>
                  <ds-button
                    variant="rounded-secondary"
                    label="Set on Map"
                    icon="map"
                    size="small"
                    @click="openMapModal"
                    class="w-full"
                  />
                  <p class="text-xs text-gray-500">
                    Click to open an interactive map to visually set the center point and zoom level
                  </p>
                </div>

                <!-- Manual input (collapsed by default) -->
                <div class="border-t pt-4">
                  <button
                    type="button"
                    @click="showManualInputs = !showManualInputs"
                    class="tw-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                  >
                    <Icon :name="showManualInputs ? 'chevron-down' : 'chevron-right'" size="14"/>
                    Manual Input
                  </button>

                  <div v-if="showManualInputs" class="mt-3 space-y-4">
                    <!-- Default Zoom Level -->
                    <div class="space-y-2">
                      <label for="zoom" class="block text-sm font-medium text-gray-700">
                        Default Zoom Level
                      </label>
                      <ds-input
                        id="zoom"
                        v-model="mapConfig.zoom"
                        type="number"
                        :min="1"
                        :max="18"
                        :class="zoomError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''"
                        @blur="validateZoom"
                      />
                      <p v-if="zoomError" class="text-red-600 text-sm">{{ zoomError }}</p>
                    </div>

                    <!-- Map Center Coordinates -->
                    <div class="grid grid-cols-2 gap-4">
                      <div class="space-y-2">
                        <label for="lat" class="block text-sm font-medium text-gray-700">
                          Latitude
                        </label>
                        <ds-input
                          id="lat"
                          v-model="mapConfig.lat"
                          type="number"
                          step="0.000001"
                          :min="-90"
                          :max="90"
                          :class="latError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''"
                          @blur="validateLat"
                        />
                        <p v-if="latError" class="text-red-600 text-sm">{{ latError }}</p>
                      </div>
                      <div class="space-y-2">
                        <label for="lng" class="block text-sm font-medium text-gray-700">
                          Longitude
                        </label>
                        <ds-input
                          id="lng"
                          v-model="mapConfig.lng"
                          type="number"
                          step="0.000001"
                          :min="-180"
                          :max="180"
                          :class="lngError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''"
                          @blur="validateLng"
                        />
                        <p v-if="lngError" class="text-red-600 text-sm">{{ lngError }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Marker Settings -->
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-2">
                  <label for="markerIcon" class="block text-sm font-medium text-gray-700">
                    Marker Icon
                  </label>
                  <select
                    id="markerIcon"
                    v-model="mapConfig.markerIcon"
                    class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    <option value="default">Default</option>
                    <option value="pin">Pin</option>
                    <option value="circle">Circle</option>
                    <option value="star">Star</option>
                    <option value="triangle">Triangle</option>
                  </select>
                </div>
                <div class="space-y-2">
                  <label for="markerIcon" class="block text-sm font-medium text-gray-700">
                    Default Marker Color
                  </label>
                  <input class="w-full h-8" type="color" v-model="mapConfig.markerColor" @update:modelValue="updateMarkerColor"/>
                </div>
              </div>

              <!-- Country Preferences -->
              <div class="grid grid-cols-2 gap-4">
                <div class="space-y-2">
                  <label for="preferredCountry" class="block text-sm font-medium text-gray-700">
                    Preferred Country
                    <Icon name="information" size="14"
                          v-tooltip="'When companies have multiple locations, prioritize showing their office in this country on the map instead of their headquarters location.'"/>
                  </label>
                  <dropdown
                    allow-clear
                    :options="COUNTRIES"
                    search
                    v-model="mapConfig.preferredCountry"
                    is-simplified
                    new-append-icon="chevron-down"
                    @update:model-value="updatePreferredCountry"
                  />
                </div>
                <div class="space-y-2">
                  <label for="preferredConnectionsCountry" class="block text-sm font-medium text-gray-700">
                    Preferred Connections Country
                    <Icon name="information" size="14"
                          v-tooltip="'Highlight and filter companies that have business connections or relationships with companies from this country.'"/>
                  </label>
                  <dropdown
                    allow-clear
                    :options="COUNTRIES"
                    search
                    v-model="mapConfig.preferredConnectionsCountry"
                    is-simplified
                    new-append-icon="chevron-down"
                    @update:model-value="updatePreferredConnectionsCountry"
                  />
                </div>
              </div>

              <!-- Default Language -->
              <div class="space-y-2">
                <label for="defaultLanguage" class="block text-sm font-medium text-gray-700">
                  Default Language
                  <Icon name="information" size="14"
                        v-tooltip="'Set the default language for the map interface. This will be used as the primary language for labels and content.'"/>
                </label>
                <dropdown
                  id="defaultLanguage"
                  :options="languageOptions"
                  v-model="mapConfig.defaultLanguage"
                  is-simplified
                  new-append-icon="chevron-down"
                />
              </div>

              <!-- Spotting Area -->
              <div class="space-y-2">
                <label for="spottingArea" class="block text-sm font-medium text-gray-700">
                  Spotting Area
                </label>
                <dropdown
                  id="spottingArea"
                  is-simplified
                  new-append-icon="chevron-down"
                  :options="availableSpottingAreas"
                  v-model="selectedSpottingAreaId"
                />

                <!-- Allowed facet filters -->
              </div>

              <div class="space-y-2">
                <Checkbox :model-value="mapConfig.showContent" v-model="mapConfig.showContent"
                          label="Do you want to enable content on the map? This will add an extra tab"/>
              </div>

              <div class="space-y-2" v-if="mapConfig.showContent">
                <label for="spottingArea" class="block text-sm font-medium text-gray-700">
                  What would you call the tab where spotting area content is shown?
                </label>
                <dropdown :options="contentLabelOptions" v-model="mapConfig.contentLabel" is-simplified/>
              </div>

              <div class="space-y-2" v-if="mapConfig.showContent">
                <label class="block text-sm font-medium text-gray-700">
                  Which tab should be visible when the map is loaded?
                  <Icon name="information" size="14" class="ml-1"
                        v-tooltip="'Choose which tab should be shown first when the map loads'"/>
                </label>


                <div class="w-full filter-group membership-group tw-flex justify-center align-center mx-auto">
                  <div class="filter-buttons w-full">
                    <button
                      @click="updateDefaultTab('content')"
                      :class="['filter-btn w-1/2', { active: mapConfig.defaultTab === 'content' }]"
                    >
                      {{ contentLabelOptions.filter(val => val.value === mapConfig.contentLabel)[0].label }}
                    </button>
                    <button
                      @click="updateDefaultTab('actors')"
                      :class="['filter-btn w-1/2', { active: mapConfig.defaultTab === 'actors' }]"
                    >
                      Actors
                    </button>
                  </div>
                </div>
              </div>

              <!-- Filter Type Selection -->
              <div class="space-y-2 !mt-6">
                <label class="block text-sm font-medium text-gray-700 mt-4">
                  Map Filters
                  <Icon name="information" size="14"
                        v-tooltip="'Configure Report Field Filters and Taxonomy Filters to control what companies appear on the map'"/>
                </label>

                <!-- Both filter sections in a row -->
                <div class="tw-flex flex-col gap-4">
                  <!-- Report Field Filters Section -->
                  <div class="flex-1 border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      @click="reportFieldsExpanded = !reportFieldsExpanded"
                      class="tw-flex items-center justify-between w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    >
                      <span class="text-sm font-medium text-gray-700">Report Field Filters</span>
                      <Icon :name="reportFieldsExpanded ? 'chevron-up' : 'chevron-down'" size="16" class="text-gray-500"/>
                    </button>
                    <div v-show="reportFieldsExpanded" class="p-4">
                      <p class="text-xs text-gray-600 mb-3">
                        Select reports and their fields to filter what companies appear on the map. Only companies with selected values for these fields will be visible.
                      </p>

                      <!-- Report Selectors -->
                      <div class="space-y-4">
                        <div
                          v-for="(reportConfig, index) in visibleReportFilters"
                          :key="index"
                          class="border border-gray-200 rounded-lg p-4 bg-gray-50"
                        >
                          <div class="tw-flex items-center justify-between mb-4">
                            <h4 class="text-sm font-medium text-gray-700">Report Filter {{ index + 1 }}</h4>
                            <ds-button
                              v-if="index > 0"
                              variant="ghost"
                              size="small"
                              icon="x"
                              @click="removeReportFilter(index)"
                              class="text-red-600 hover:text-red-700"
                            />
                          </div>

                          <div class="space-y-4">
                            <!-- Report Template Selection -->
                            <div>
                              <label class="block text-xs font-medium text-gray-600 mb-1">Report Template</label>
                              <div class="tw-flex items-center gap-2">
                                <div class="flex-1">
                                  <dropdown
                                    :options="availableReportTemplates"
                                    search
                                    v-model="reportConfig.reportTemplateId"
                                    @update:modelValue="onReportTemplateChange(index, $event)"
                                    placeholder="Select a report template..."
                                    is-simplified
                                    new-append-icon="chevron-down"
                                  />
                                </div>
                                <ds-button
                                  v-if="reportConfig.reportTemplateId"
                                  variant="ghost"
                                  size="small"
                                  icon="x"
                                  @click="clearReportTemplate(index)"
                                  class="text-gray-500 hover:text-gray-700"
                                  title="Clear selection"
                                />
                              </div>
                            </div>

                            <!-- Fields from selected report -->
                            <div v-if="reportConfig.reportTemplateId" class="space-y-3">
                              <div class="border-t pt-3">
                                <label class="block text-xs font-medium text-gray-600 mb-2">Fields from this report</label>

                                <div
                                  v-for="(fieldConfig, fieldIndex) in reportConfig.fields"
                                  :key="fieldIndex"
                                  class="border border-gray-100 rounded p-3 bg-white"
                                >
                                  <div class="tw-flex items-center justify-between mb-2">
                                    <div class="flex-1">
                                      <label class="block text-xs font-medium text-gray-600 mb-1">Field</label>
                                      <dropdown
                                        :options="getAvailableFieldsForTemplateExcludingSelected(reportConfig.reportTemplateId, index, fieldIndex).map(f => ({ label: f.label, value: f.id }))"
                                        search
                                        v-model="fieldConfig.fieldId"
                                        @update:modelValue="onFieldChange(index, fieldIndex, $event)"
                                        placeholder="Select a field..."
                                        is-simplified
                                        new-append-icon="chevron-down"
                                      />
                                    </div>
                                    <ds-button
                                      v-if="reportConfig.fields.length > 1"
                                      variant="ghost"
                                      size="small"
                                      icon="x"
                                      @click="removeField(index, fieldIndex)"
                                      class="text-red-500 hover:text-red-700 ml-2"
                                    />
                                  </div>

                                  <!-- Value Selection for this field -->
                                  <div>
                                    <label class="block text-xs text-gray-500 mb-1">Allowed Values with Colors (Note: only multi select fields are configurable)</label>
                                    <div class="space-y-1">
                                      <div
                                        v-for="option in getFieldOptions(fieldConfig.fieldId)"
                                        :key="option.value"
                                        class="tw-flex items-center gap-2 border border-gray-300 bg p-1 rounded bg-gray-50"
                                      >
                                        <input
                                          :id="`report_${index}_field_${fieldIndex}_${option.value}`"
                                          type="checkbox"
                                          :value="option.value"
                                          v-model="fieldConfig.allowedValues"
                                          @change="onFieldValueToggle(index, fieldIndex, option.value)"
                                          class="h-3 w-3 mb-2 text-primary focus:ring-primary border-gray-300 rounded"
                                        />
                                        <label
                                          :for="`report_${index}_field_${fieldIndex}_${option.value}`"
                                          class="text-xs text-gray-700 flex-1"
                                        >
                                          {{ option.label }}
                                        </label>
                                        <input
                                          v-if="fieldConfig.allowedValues.includes(option.value)"
                                          type="color"
                                          v-model="fieldConfig.colorAssignments[option.value]"
                                          @input="onFieldValueColorChange(index, fieldIndex, option.value)"
                                          class="w-6 h-6 rounded border border-gray-300 cursor-pointer"
                                          :title="`Color for ${option.label}`"
                                        />
                                      </div>
                                    </div>

                                    <div class="mt-1">
                                      <ds-button
                                        variant="rounded-secondary"
                                        size="small"
                                        :label="fieldConfig.allowedValues.length === getFieldOptions(fieldConfig.fieldId).length ? 'Deselect All' : 'Select All'"
                                        @click="toggleAllFieldValues(index, fieldIndex)"
                                        class="text-xs"
                                      />
                                    </div>
                                  </div>
                                </div>

                                <!-- Add Field Button -->
                                <ds-button
                                  variant="rounded-secondary"
                                  size="small"
                                  icon="plus"
                                  label="Add Field"
                                  @click="addFieldToReport(index)"
                                  class="text-blue-600 hover:text-blue-700 mt-2"
                                />
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Add Report Filter Button -->
                        <ds-button
                          variant="rounded-secondary"
                          size="small"
                          icon="plus"
                          label="Add Report Filter"
                          @click="addReportFilter"
                          class="text-blue-600 hover:text-blue-700"
                        />

                        <p class="text-xs text-gray-500">
                          Only companies that have one of the selected values for ALL configured fields will appear on the map.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Taxonomy Filters Section -->
                  <div class="flex-1 border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      @click="taxonomyFiltersExpanded = !taxonomyFiltersExpanded"
                      class="tw-flex items-center justify-between w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                    >
                      <span class="text-sm font-medium text-gray-700">Taxonomy Filters</span>
                      <Icon :name="taxonomyFiltersExpanded ? 'chevron-up' : 'chevron-down'" size="16" class="text-gray-500"/>
                    </button>
                    <div v-show="taxonomyFiltersExpanded" class="p-4 space-y-4">
                      <p class="text-xs text-gray-600 mb-3">
                        Select taxonomy values to filter what companies appear on the map. Only companies with at least one selected value will be visible.
                      </p>

                      <!-- Dynamic Taxonomy Sections -->
                      <template v-for="taxonomyKey in allTaxonomyKeys" :key="taxonomyKey">
                        <div
                          v-if="taxonomyFilters[taxonomyKey] && taxonomyFilters[taxonomyKey].length > 0"
                          class="border border-gray-200 rounded-lg p-4 bg-gray-50"
                        >
                          <h4 class="text-sm font-medium text-gray-700 mb-3">
                            {{ taxonomyLabels[taxonomyKey] }} ({{ taxonomyFilters[taxonomyKey]?.length || 0 }})
                          </h4>
                          <div class="space-y-2">
                            <div
                              v-for="item in taxonomyFilters[taxonomyKey]"
                              :key="item.id"
                              class="border border-gray-300 rounded bg-white"
                            >
                              <div class="tw-flex items-center gap-2 p-2">
                                <button
                                  v-if="item.children && item.children.length > 0"
                                  type="button"
                                  @click="item.expanded = !item.expanded"
                                  class="text-gray-500 hover:text-gray-700"
                                >
                                  <Icon :name="item.expanded ? 'chevron-down' : 'chevron-right'" size="18"/>
                                </button>
                                <span v-else class="w-3"></span>
                                <input
                                  :id="`${taxonomyKey}_${item.id}`"
                                  type="checkbox"
                                  v-model="item.selected"
                                  class="h-3 w-3 text-primary focus:ring-primary border-gray-300 rounded"
                                />
                                <label
                                  :for="`${taxonomyKey}_${item.id}`"
                                  class="text-xs text-gray-700 flex-1"
                                >
                                  {{ item.name }}
                                  <span v-if="item.children && item.children.length > 0" class="text-gray-400">
                                    ({{ item.children.length }})
                                  </span>
                                </label>
                                <input
                                  v-if="item.selected"
                                  type="color"
                                  disabled
                                  v-model="item.hex"
                                  class="w-6 h-6 rounded border border-gray-300 cursor-not-allowed"
                                  :title="`Color for ${item.name}`"
                                />
                              </div>
                              <!-- Children -->
                              <div
                                v-if="item.expanded && item.children && item.children.length > 0"
                                class="pl-8 pb-2 pr-2 space-y-1 border-t border-gray-200 bg-gray-50"
                              >
                                <div
                                  v-for="child in item.children"
                                  :key="child.id"
                                  class="tw-flex items-center gap-2 p-1"
                                >
                                  <input
                                    :id="`${taxonomyKey}_child_${child.id}`"
                                    type="checkbox"
                                    v-model="child.selected"
                                    class="h-3 w-3 text-primary focus:ring-primary border-gray-300 rounded"
                                  />
                                  <label
                                    :for="`${taxonomyKey}_child_${child.id}`"
                                    class="text-xs text-gray-600 flex-1"
                                  >
                                    {{ child.name }}
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                          <ds-button
                            variant="rounded-secondary"
                            size="small"
                            :label="isTaxonomyAllSelected(taxonomyKey) ? 'Deselect All' : 'Select All'"
                            @click="toggleAllTaxonomy(taxonomyKey)"
                            class="text-xs mt-2"
                          />
                        </div>
                      </template>

                      <p class="text-xs text-gray-500">
                        Companies with at least one selected taxonomy value will appear on the map.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Showcase Category -->
              <!--              <div class="space-y-2">
                              <label class="block text-sm font-medium text-gray-700">
                                Showcase Category
                                <Icon name="information" size="14"
                                      v-tooltip="'Select a knowledge base category to showcase posts as an additional tab on the map. Posts from this category will be visible to map users.'"/>
                              </label>
                              <dropdown
                                :options="availableKnowledgeBaseCategories"
                                search
                                v-model="selectedKnowledgeBaseCategoryId"
                                placeholder="Select a category..."
                                is-simplified
                                new-append-icon="chevron-down"
                                clearable
                              />
                              <p class="text-xs text-gray-500">
                                Users can configure individual posts to show or hide on the map from the post editor.
                              </p>
                            </div>-->

              <!-- Allowed Domains -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">
                  Allowed Domains
                  <Icon class="ml-1" name="information" size="14"
                        v-tooltip="'Enter the domains where this map will be embedded (e.g., example.com or subdomain.example.com). This is required for security and will be used to set CORS headers.'"/>
                </label>

                <!-- Domain Inputs -->
                <div class="space-y-2">
                  <div
                    v-for="(domain, index) in allowedDomains"
                    :key="index"
                    class="tw-flex items-center gap-2"
                  >
                    <ds-input
                      v-model="allowedDomains[index]"
                      :placeholder="index === 0 ? 'example.com' : 'additional-domain.com'"
                      :class="getDomainError(index) ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''"
                      @blur="validateDomains"
                      is-simplified
                      class="flex-1"
                    />
                    <ds-button
                      v-if="index > 0"
                      variant="ghost"
                      size="small"
                      icon="x"
                      @click="removeDomain(index)"
                      class="text-red-600 hover:text-red-700"
                    />
                  </div>

                  <!-- Add Domain Button -->
                  <ds-button
                    variant="rounded-secondary"
                    size="small"
                    icon="plus"
                    label="Add Domain"
                    @click="addDomain"
                    class="text-blue-600 hover:text-blue-700"
                  />
                </div>

                <div v-if="domainsError" class="text-red-600 text-sm">{{ domainsError }}</div>
                <p class="text-xs text-gray-500">
                  Only these domains will be allowed to embed your map. Don't include http:// or https://
                </p>
              </div>

              <!-- User Interface Options -->
              <div class="space-y-4">
                <label class="block text-sm font-medium text-gray-700">
                  User Interface Options
                </label>

                <!-- Add Organisation Button -->
                <div class="tw-flex items-center gap-3">
                  <input
                    id="showAddOrganisationButton"
                    type="checkbox"
                    v-model="mapConfig.showAddOrganisationButton"
                    class="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mb-2"
                  />
                  <label for="showAddOrganisationButton" class="text-sm text-gray-700">
                    Show "Add your organisation" button
                  </label>
                </div>
                <p class="text-xs text-gray-500 ml-7 !mt-0">
                  When enabled, users can request to add their organisation to the map
                </p>

                <!-- Simplified Actor Filters (DEPRECATED - use Taxonomy Filters instead) -->
                <!-- This section is hidden as taxonomy filters now replace simplified filter controls -->
                <div v-if="false" class="space-y-2">
                  <label class="block text-sm font-medium text-gray-700">
                    Visible Filter Controls
                    <span class="text-orange-500 text-xs ml-1">(Deprecated)</span>
                    <Icon name="information" size="14" class="ml-1"
                          v-tooltip="'Deprecated: Use Taxonomy Filters instead'"/>
                  </label>
                  <dropdown
                    :options="availableSimplifiedFilterOptions"
                    multiple
                    v-model="mapConfig.simplifiedFilterControls"
                    placeholder="Select visible filters..."
                    is-simplified
                    new-append-icon="chevron-down"
                    class="w-full"
                    search
                  />
                  <p class="text-xs text-gray-500">
                    Choose which filter controls users can see when browsing organisations on the map
                  </p>
                </div>
              </div>

              <!-- Map Status -->
              <div class="space-y-2">
                <label for="mapStatus" class="block text-sm font-medium text-gray-700">
                  Map Status
                </label>
                <div class="tw-flex items-center gap-4">
                  <div class="tw-flex items-center gap-2">
                    <input
                      id="statusActive"
                      type="radio"
                      value="active"
                      v-model="mapConfig.status"
                      class="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <label for="statusActive" class="text-sm text-gray-700 mt-2">Active</label>
                  </div>
                  <div class="tw-flex items-center gap-2">
                    <input
                      id="statusInactive"
                      type="radio"
                      value="inactive"
                      v-model="mapConfig.status"
                      class="h-4 w-4 text-gray-400 focus:ring-gray-400 border-gray-300"
                    />
                    <label for="statusInactive" class="text-sm text-gray-700 mt-2">Inactive</label>
                  </div>
                  <div class="tw-flex items-center gap-2">
                    <input
                      id="statusDraft"
                      type="radio"
                      value="draft"
                      v-model="mapConfig.status"
                      class="h-4 w-4 text-yellow-500 focus:ring-yellow-400 border-gray-300"
                    />
                    <label for="statusDraft" class="text-sm text-gray-700 mt-2">Draft</label>
                  </div>
                </div>
                <p class="text-xs text-gray-500">
                  Only active maps will be accessible via the embed URL
                </p>
              </div>

              <!-- Save Button -->
              <div class="pt-4 border-t">
                <ds-button
                  class="w-full"
                  :label="saving ? 'Saving...' : 'Save Changes'"
                  @click="handleSave"
                  variant="rounded"
                  :disabled="saving || !isFormValid"
                />
              </div>
            </div>
          </div>

          <!-- Version History -->
          <div v-if="!isNew" class="bg-white rounded-lg shadow-sm border">
            <div class="p-6 border-b">
              <div class="tw-flex items-center justify-between">
                <div>
                  <h2 class="text-xl font-semibold">Version History</h2>
                  <p class="text-gray-600 mt-1 text-sm">
                    View and restore previous versions
                  </p>
                </div>
                <ds-button
                  variant="rounded-secondary"
                  size="small"
                  :label="showVersions ? 'Hide' : 'Show'"
                  @click="toggleVersions"
                />
              </div>
            </div>
            <div v-if="showVersions" class="p-6">
              <div v-if="loadingVersions" class="text-center py-4">
                <p class="text-gray-500">Loading versions...</p>
              </div>
              <div v-else-if="versions.length === 0" class="text-center py-4">
                <p class="text-gray-500">No versions available yet</p>
              </div>
              <div v-else class="space-y-2 max-h-96 overflow-y-auto">
                <div
                  v-for="version in versions"
                  :key="version.id"
                  class="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div class="tw-flex items-center justify-between">
                    <div class="tw-flex items-center gap-3">
                      <div class="tw-flex flex-col">
                        <div class="tw-flex items-center gap-2">
                          <span class="font-semibold text-sm">Version {{ version.version_number }}</span>
                          <span v-if="version.label" class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                            {{ version.label }}
                          </span>
                        </div>
                        <span class="text-xs text-gray-500">
                          {{ formatDate(version.created_at) }} by {{ version.created_by.name }}
                        </span>
                      </div>
                    </div>
                    <div class="tw-flex items-center gap-2">
                      <ds-button
                        variant="ghost"
                        size="small"
                        label="Preview"
                        @click="previewVersion(version)"
                      />
                      <ds-button
                        variant="rounded-secondary"
                        size="small"
                        label="Restore"
                        @click="restoreVersion(version.id)"
                        :disabled="restoringVersion"
                      />
                      <ds-button
                        variant="ghost"
                        size="small"
                        label="Label"
                        @click="openLabelDialog(version)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Version Preview Modal -->
        <div v-if="showPreviewModal" class="fixed inset-0 z-50 overflow-y-auto" style="background-color: rgba(0, 0, 0, 0.5);">
          <div class="tw-flex items-center justify-center min-h-screen p-4">
            <div class="bg-white rounded-lg shadow-xl" style="width: 95vw; max-width: 1400px; max-height: 90vh; display: flex; flex-direction: column;">
              <!-- Header -->
              <div class="tw-flex items-center justify-between p-6 border-b">
                <h2 class="text-xl font-semibold">Version Preview</h2>
                <button @click="closePreviewModal" class="text-gray-400 hover:text-gray-600 text-2xl font-bold">&times;</button>
              </div>

              <!-- Body -->
              <div class="p-6 overflow-y-auto" style="flex: 1;">
                <div v-if="loadingPreview" class="text-center py-8">
                  <p class="text-gray-500">Loading version details...</p>
                </div>
                <div v-else-if="previewData" class="space-y-6">
                  <div class="border-b pb-4">
                    <h3 class="text-lg font-semibold">Version {{ previewData.version_number }}</h3>
                    <p class="text-sm text-gray-500">
                      Created {{ formatDate(previewData.created_at) }} by {{ previewData.created_by.name }}
                    </p>
                    <p v-if="previewData.label" class="text-sm text-blue-600 mt-1">{{ previewData.label }}</p>
                  </div>

                  <div class="grid grid-cols-2 gap-6">
                    <!-- Current Version -->
                    <div>
                      <h4 class="font-semibold mb-3 text-gray-700">Current Version</h4>
                      <div class="space-y-3 text-sm">
                        <div>
                          <span class="font-medium text-gray-600">Name:</span>
                          <span class="ml-2">{{ mapName }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-600">Status:</span>
                          <span class="ml-2 capitalize">{{ mapConfig.status }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-600">Zoom:</span>
                          <span class="ml-2">{{ mapConfig.zoom }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-600">Center:</span>
                          <span class="ml-2">{{ mapConfig.lat }}, {{ mapConfig.lng }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-600">Marker Color:</span>
                          <span class="ml-2 tw-flex items-center gap-2">
                        <span :style="{backgroundColor: mapConfig.markerColor}" class="inline-block w-4 h-4 rounded"></span>
                        {{ mapConfig.markerColor }}
                      </span>
                        </div>
                        <div v-if="selectedSpottingAreaId">
                          <span class="font-medium text-gray-600">Spotting Area:</span>
                          <span class="ml-2">{{ getSpottingAreaNameById(selectedSpottingAreaId) }}</span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-600">Show Content:</span>
                          <span class="ml-2">{{ mapConfig.showContent ? 'Yes' : 'No' }}</span>
                        </div>
                        <div v-if="mapConfig.showContent">
                          <span class="font-medium text-gray-600">Content Label:</span>
                          <span class="ml-2">{{ mapConfig.contentLabel }}</span>
                        </div>
                        <div v-if="mapConfig.preferredCountry">
                          <span class="font-medium text-gray-600">Preferred Country:</span>
                          <span class="ml-2">{{ mapConfig.preferredCountry }}</span>
                        </div>
                        <div v-if="allowedDomains && allowedDomains.length > 0 && allowedDomains.some(d => d.trim())">
                          <span class="font-medium text-gray-600">Allowed Domains:</span>
                          <span class="ml-2">{{ allowedDomains.filter(d => d.trim()).join(', ') }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Version Being Previewed -->
                    <div class="bg-blue-50 p-4 rounded-lg">
                      <h4 class="font-semibold mb-3 text-blue-900">Version {{ previewData.version_number }}</h4>
                      <div class="space-y-3 text-sm">
                        <div>
                          <span class="font-medium text-gray-600">Name:</span>
                          <span class="ml-2" :class="{'text-orange-600 font-semibold': previewData.map_data.name !== mapName}">
                        {{ previewData.map_data.name }}
                      </span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-600">Status:</span>
                          <span class="ml-2 capitalize" :class="{'text-orange-600 font-semibold': previewData.map_data.status !== mapConfig.status}">
                        {{ previewData.map_data.status }}
                      </span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-600">Zoom:</span>
                          <span class="ml-2" :class="{'text-orange-600 font-semibold': parseInt(previewData.map_data.zoom) !== parseInt(mapConfig.zoom)}">
                        {{ previewData.map_data.zoom }}
                      </span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-600">Center:</span>
                          <span class="ml-2" :class="{'text-orange-600 font-semibold': hasCoordinateChanged(previewData.map_data)}">
                        {{ previewData.map_data.lat }}, {{ previewData.map_data.lng }}
                      </span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-600">Marker Color:</span>
                          <span class="ml-2 tw-flex items-center gap-2" :class="{'text-orange-600 font-semibold': previewData.map_data.marker_color !== mapConfig.markerColor}">
                        <span :style="{backgroundColor: previewData.map_data.marker_color}" class="inline-block w-4 h-4 rounded"></span>
                        {{ previewData.map_data.marker_color }}
                      </span>
                        </div>
                        <div v-if="previewData.map_data.spotting_area_id">
                          <span class="font-medium text-gray-600">Spotting Area:</span>
                          <span class="ml-2" :class="{'text-orange-600 font-semibold': previewData.map_data.spotting_area_id !== selectedSpottingAreaId}">
                        {{ getSpottingAreaNameById(previewData.map_data.spotting_area_id) }}
                      </span>
                        </div>
                        <div>
                          <span class="font-medium text-gray-600">Show Content:</span>
                          <span class="ml-2" :class="{'text-orange-600 font-semibold': Boolean(previewData.map_data.show_content) !== Boolean(mapConfig.showContent)}">
                        {{ Boolean(previewData.map_data.show_content) ? 'Yes' : 'No' }}
                      </span>
                        </div>
                        <div v-if="previewData.map_data.show_content">
                          <span class="font-medium text-gray-600">Content Label:</span>
                          <span class="ml-2" :class="{'text-orange-600 font-semibold': previewData.map_data.content_label !== mapConfig.contentLabel}">
                        {{ previewData.map_data.content_label }}
                      </span>
                        </div>
                        <div v-if="previewData.map_data.preferred_country">
                          <span class="font-medium text-gray-600">Preferred Country:</span>
                          <span class="ml-2" :class="{'text-orange-600 font-semibold': previewData.map_data.preferred_country !== mapConfig.preferredCountry}">
                        {{ previewData.map_data.preferred_country }}
                      </span>
                        </div>
                        <div v-if="previewData.map_data.allowed_domains && previewData.map_data.allowed_domains.length > 0">
                          <span class="font-medium text-gray-600">Allowed Domains:</span>
                          <span class="ml-2"
                                :class="{'text-orange-600 font-semibold': JSON.stringify(previewData.map_data.allowed_domains) !== JSON.stringify(allowedDomains.filter(d => d.trim()))}">
                        {{ Array.isArray(previewData.map_data.allowed_domains) ? previewData.map_data.allowed_domains.join(', ') : previewData.map_data.allowed_domains }}
                      </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Report Field Filters Comparison -->
                  <div class="border-t pt-4 mt-4">
                    <h4 class="font-semibold mb-3">Report Field Filters</h4>
                    <div class="grid grid-cols-2 gap-6">
                      <!-- Current Filters -->
                      <div>
                        <h5 class="text-sm font-medium text-gray-700 mb-2">Current Filters</h5>
                        <div v-if="visibleReportFilters.length === 0" class="text-sm text-gray-500 italic">
                          No filters configured
                        </div>
                        <div v-else class="space-y-3">
                          <div v-for="(filter, index) in visibleReportFilters" :key="'current-' + index" class="border rounded p-2 bg-white">
                            <div class="text-xs font-medium text-gray-600 mb-1">
                              Template: {{ getReportTemplateName(filter.reportTemplateId) }}
                            </div>
                            <div v-for="(field, fIndex) in filter.fields" :key="'field-' + fIndex" class="ml-2 text-xs">
                              <div class="font-medium">{{ getReportFieldName(field.fieldId) }}</div>
                              <div class="text-gray-600 ml-2">
                                Values: {{ field.allowedValues.join(', ') }}
                              </div>
                              <div v-if="field.colorAssignments && Object.keys(field.colorAssignments).length > 0" class="tw-flex flex-wrap gap-1 mt-1 ml-2">
                            <span v-for="(color, value) in field.colorAssignments" :key="value" class="tw-flex items-center gap-1 text-xs">
                              <span :style="{backgroundColor: color}" class="inline-block w-3 h-3 rounded"></span>
                              <span>{{ value }}</span>
                            </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Version Filters -->
                      <div class="bg-blue-50 p-3 rounded-lg">
                        <h5 class="text-sm font-medium text-blue-900 mb-2">Version {{ previewData.version_number }} Filters</h5>
                        <div v-if="previewData.report_field_filters.length === 0" class="text-sm text-gray-500 italic">
                          No filters configured
                        </div>
                        <div v-else class="space-y-3">
                          <div v-for="(filter, index) in previewData.report_field_filters" :key="'version-' + index"
                               class="border rounded p-2 bg-white"
                               :class="{'ring-2 ring-orange-400': hasFilterChanged(index)}">
                            <div class="text-xs font-medium text-gray-600 mb-1">
                              Template: {{ getReportTemplateName(filter.ecosystem_report_template_id) }}
                            </div>
                            <div class="ml-2 text-xs">
                              <div class="font-medium">{{ getReportFieldName(filter.ecosystem_report_field_id) }}</div>
                              <div class="text-gray-600 ml-2">
                                Values: {{ parseAllowedValues(filter.allowed_values).join(', ') }}
                              </div>
                              <div v-if="filter.allowed_values_colors" class="tw-flex flex-wrap gap-1 mt-1 ml-2">
                            <span v-for="(color, value) in getColorAssignments(filter.allowed_values, filter.allowed_values_colors)" :key="value"
                                  class="tw-flex items-center gap-1 text-xs">
                              <span :style="{backgroundColor: color}" class="inline-block w-3 h-3 rounded border border-gray-300"></span>
                              <span>{{ value }} ({{ color }})</span>
                            </span>
                              </div>
                              <div v-else class="text-gray-400 text-xs ml-2">
                                (No colors assigned)
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="bg-gray-50 p-3 rounded text-xs text-gray-600">
                    <strong>Note:</strong> Orange highlighted values indicate changes from the current version.
                  </div>
                </div>
              </div>

              <!-- Footer -->
              <div class="tw-flex justify-end gap-3 p-6 border-t bg-gray-50">
                <ds-button
                  variant="ghost"
                  label="Close"
                  @click="closePreviewModal"
                />
                <ds-button
                  variant="rounded"
                  label="Restore This Version"
                  @click="restoreVersionFromPreview"
                  :disabled="restoringVersion"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Preview Panel -->
        <div class="w-2/3 sticky top-8 h-fit max-h-[calc(100vh-120px)] overflow-y-auto" :key="livePreviewKey"
        >
          <div class="space-y-4">
            <div class="bg-white rounded-lg shadow-sm border">
              <div class="p-6 border-b">
                <h2 class="text-xl font-semibold tw-flex items-center gap-2">
                  <Icon name="eye" class="h-5 w-5"/>
                  Live Preview
                  <span v-if="hasUnsavedChanges" class="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">Unsaved changes</span>
                </h2>
                <p class="text-gray-600 mt-1">
                  See how your map will appear when embedded
                </p>
              </div>
              <div class="p-6">
                <!-- Map Preview -->
                <div class="bg-gray-100 rounded-lg border overflow-hidden">
                  <!-- Live embed iframe with current form values -->
                  <div
                    v-if="livePreviewEmbedUrl"
                    ref="mapContainer"
                    class="w-full h-[600px] bg-gray-200 rounded-lg relative overflow-hidden"
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      :src="livePreviewEmbedUrl"
                      frameborder="0"
                      allowfullscreen
                      sandbox="allow-scripts allow-same-origin"
                      @error="handleIframeError"
                      @load="handleIframeLoad"
                    ></iframe>
                    <div v-if="iframeLoading" class="absolute inset-0 bg-white bg-opacity-75 tw-flex items-center justify-center">
                      <div class="text-center">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                        <div class="text-sm text-gray-600">Loading map preview...</div>
                      </div>
                    </div>
                    <div v-if="iframeError" class="absolute inset-0 bg-red-50 tw-flex items-center justify-center border-2 border-red-200">
                      <div class="text-center text-red-700 p-4">
                        <div class="text-lg mb-2">🔒 {{ corsError ? 'CORS Error' : 'SSL Certificate Error' }}</div>
                        <div class="text-sm mb-4">
                          {{ corsError ? 'The embed endpoint has CORS restrictions for local development' : 'The embed URL has SSL certificate issues for local development' }}
                        </div>
                        <div class="space-y-2">
                          <ds-button
                            variant="outline"
                            size="small"
                            label="Retry"
                            @click="retryEmbed"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Fallback placeholder -->
                  <div v-else class="bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 h-96 tw-flex items-center justify-center">
                    <div class="text-center">
                      <Icon name="map" class="w-12 h-12 text-gray-400 mx-auto mb-3"/>
                      <h3 class="text-lg font-medium text-gray-900 mb-2">
                        Map Preview
                      </h3>
                      <p class="text-gray-500 mb-4">
                        {{ isNew ? 'Save your map first to see the live preview' : 'Configure your spotting area to see the preview' }}
                      </p>
                      <div v-if="selectedSpottingAreaId && isNew" class="text-xs text-gray-500">
                        Spotting area selected. Save to generate preview.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Embed Code -->
            <div class="bg-white rounded-lg shadow-sm border">
              <div class="p-6 border-b">
                <h2 class="text-xl font-semibold tw-flex items-center gap-2">
                  <Icon name="code" class="h-5 w-5"/>
                  Embed Code

                  {{ $store.getters.url }}
                </h2>
                <p class="text-gray-600 mt-1">
                  Copy this code to embed your map
                </p>
              </div>
              <div class="p-6" v-if="map && map.embedUrl">
                <h2 class="text-xl font-semibold tw-flex items-center gap-2">
                  <a target="_blank" :href="map.embedUrl">Preview the map here</a>
                </h2>
              </div>
              <div class="p-6 space-y-6">
                <!-- Option 1: Simple iframe -->
                <div class="embed-option">
                  <div class="embed-option-header">
                    <h4 class="embed-option-title">Option 1: Simple Iframe</h4>
                    <span class="embed-option-badge">Easiest</span>
                  </div>
                  <p class="embed-option-description">
                    Just copy and paste this code into your website. No technical knowledge required.
                  </p>
                  <div class="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <code class="text-green-400 text-sm whitespace-pre">{{ embedCode || 'Save your map to generate embed code' }}</code>
                  </div>
                  <ds-button
                    class="mt-3 w-full"
                    variant="outline"
                    label="Copy Iframe Code"
                    @click="copyEmbedCode"
                    :disabled="!embedCode"
                  />
                </div>

                <!-- Option 2: JavaScript Widget -->
                <div class="embed-option">
                  <div class="embed-option-header">
                    <h4 class="embed-option-title">Option 2: JavaScript Widget</h4>
                    <span class="embed-option-badge advanced">Advanced</span>
                  </div>
                  <p class="embed-option-description">
                    Requires basic HTML knowledge.
                  </p>
                  <div class="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                    <code class="text-green-400 text-sm whitespace-pre">{{ embedCodeScript || 'Save your map to generate embed code' }}</code>
                  </div>
                  <ds-button
                    class="mt-3 w-full"
                    variant="outline"
                    label="Copy JavaScript Code"
                    @click="copyEmbedCodeScript"
                    :disabled="!embedCodeScript"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Map Selection Modal -->
    <div v-if="showMapModal" class="fixed inset-0 bg-black bg-opacity-50 tw-flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl w-[90vw] h-[80vh] max-w-6xl relative">
        <!-- Modal Header -->
        <div class="p-6 border-b tw-flex items-center justify-between">
          <h3 class="text-xl font-semibold">Set Map Center & Zoom</h3>
          <button @click="closeMapModal" class="text-gray-400 hover:text-gray-600">
            <Icon name="x" size="24"/>
          </button>
        </div>

        <!-- Modal Content -->
        <div class="p-6 h-[calc(100%-140px)]">
          <div class="tw-flex gap-6 h-full">
            <!-- Map Container -->
            <div class="flex-1">
              <div id="map-selector" class="w-full h-full bg-gray-200 rounded-lg relative">
                <!-- Leaflet map will be mounted here -->
              </div>
            </div>

            <!-- Controls Panel -->
            <div class="w-80 space-y-4">
              <div>
                <h4 class="font-medium mb-2">Instructions</h4>
                <p class="text-sm text-gray-600 mb-4">
                  Pan and zoom to set your desired map view. Click anywhere on the map to set the center point.
                </p>
              </div>

              <!-- City Search -->
              <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <label class="text-sm font-medium text-gray-700 mb-2 block">Search City</label>
                <div class="tw-flex gap-2">
                  <input
                    v-model="citySearchQuery"
                    type="text"
                    class="flex-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    placeholder="Enter city name..."
                    @keydown.enter.prevent="searchCityInModal"
                    @input="citySearchResults = []"
                  />
                  <ds-button
                    @click="searchCityInModal"
                    variant="rounded"
                    size="small"
                    icon="search"
                    :disabled="!citySearchQuery.trim() || searchingCity"
                    :loading="searchingCity"
                  />
                </div>

                <!-- City Results Dropdown -->
                <div v-if="citySearchResults.length > 0" class="mt-2 max-h-48 overflow-y-auto border border-gray-300 rounded-lg bg-white shadow-lg">
                  <button
                    v-for="city in citySearchResults"
                    :key="city.geoid"
                    @click="selectCity(city)"
                    class="w-full text-left px-3 py-2 hover:bg-primary-50 transition-colors text-sm border-b border-gray-100 last:border-b-0"
                  >
                    {{ city.display_name }}
                  </button>
                </div>
              </div>

              <div class="space-y-3">
                <div>
                  <label class="text-sm font-medium text-gray-700">Current Zoom Level</label>
                  <div class="text-lg font-mono">{{ tempMapConfig.zoom }}</div>
                </div>
                <div>
                  <label class="text-sm font-medium text-gray-700">Center Latitude</label>
                  <div class="text-sm font-mono">{{ parseFloat(tempMapConfig.lat).toFixed(6) }}</div>
                </div>
                <div>
                  <label class="text-sm font-medium text-gray-700">Center Longitude</label>
                  <div class="text-sm font-mono">{{ parseFloat(tempMapConfig.lng).toFixed(6) }}</div>
                </div>
              </div>

              <div class="pt-4 border-t">
                <ds-button
                  class="w-full mb-3"
                  label="Apply Settings"
                  @click="applyMapSettings"
                />
                <ds-button
                  class="w-full"
                  variant="outline"
                  label="Cancel"
                  @click="closeMapModal"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import DsInput from '@/components/Form/DsInput.vue'
  import * as EcosystemMapsApi from '@/Domain/EcosystemMap/Api/ecosystemMaps'
  import { fetchEcosystemMapVersion, fetchEcosystemMapVersions, restoreEcosystemMapVersion, updateEcosystemMapVersionLabel } from '@/Domain/EcosystemMap/Api/ecosystemMaps'
  import SpottingAreaMixin from '@/util/SpottingAreaMixin'
  import Dropdown from '@/components/Dropdown/Dropdown.vue'
  import { COUNTRIES } from '@/constants/countries'
  import { EcosystemReports } from '../../api/reporting'
  import ConfigMixin from '@/util/ConfigMixin'
  import Checkbox from '@/components/Form/Checkbox.vue'
  import ColorThemeSelector from '@/components/ColorThemeSelector.vue'
  import debounce from 'lodash/debounce'

  export default defineComponent({
    name: 'EcosystemMapDetail',
    mixins: [SpottingAreaMixin, ConfigMixin],
    components: {
      Checkbox,
      ColorThemeSelector,
      Dropdown,
      Icon,
      DsButton,
      DsInput
    },
    data () {
      return {
        saving: false,
        loading: true,
        mapName: '',
        apiKey: '',
        mapConfig: {
          zoom: 4,
          lat: 50.0,
          lng: 15.0,
          markerIcon: 'default',
          markerColor: '#3B82F6',
          preferredCountry: null,
          preferredConnectionsCountry: null,
          defaultLanguage: 'en',
          status: 'draft',
          showAddOrganisationButton: true,
          simplifiedFilterControls: [],
          contentLabel: 'landscapes',
          showContent: false,
          defaultTab: 'actors',
        },
        selectedSpottingAreaId: '',
        allowedDomains: [''],
        reportFieldsExpanded: false,
        taxonomyFiltersExpanded: false,
        filterType: 'report_fields', // or 'taxonomies'
        visibleReportFilters: [{
          reportTemplateId: null,
          fields: [{ fieldId: null, allowedValues: [], colorAssignments: {} }]
        }],
        taxonomyFilters: {
          categories: [],
          activities: [],
          domains: [],
          technologies: [],
          industries: [],
          memberships: [],
          stages: [],
          funnelStages: [],
          expertises: [],
          motivations: [],
          readinessLevels: [],
          processSteps: [],
          businessAspects: [],
          productFeaturesA: [],
          productFeaturesB: [],
          productFeaturesC: [],
        },
        reportTemplates: [],
        reportFields: [], // All fields indexed by template ID
        reportFieldOptions: {},
        knowledgeBaseCategories: [],
        map: null,
        error: null,
        validationErrors: {},
        touched: {},
        showManualInputs: false,
        showMapModal: false,
        tempMapConfig: {
          zoom: 4,
          lat: 50.0,
          lng: 15.0
        },
        leafletMap: null,
        citySearchQuery: '',
        searchingCity: false,
        citySearchResults: [],
        cityMarker: null,
        iframeError: false,
        corsError: false,
        iframeLoading: false,
        livePreviewKey: 0, // Force iframe refresh when config changes
        refreshDebounceTimer: null,
        showVersions: false,
        versions: [],
        loadingVersions: false,
        restoringVersion: false,
        showPreviewModal: false,
        loadingPreview: false,
        previewData: null,
        previewVersionId: null
      }
    },
    computed: {
      contentLabelOptions () {
        // [{value: 'landscapes', label: 'Landscapes'},'Showcases','Use Cases', 'News','Reports']
        return [
          {
            value: 'landscapes',
            label: 'Landscapes',
          },
          {
            value: 'showcases',
            label: 'Showcases',
          },
          {
            value: 'use_cases',
            label: 'Use cases',
          },
          {
            value: 'news',
            label: 'News',
          },
          {
            value: 'reports',
            label: 'Reports',
          },
        ]
      },
      defaultTabOptions () {
        return [
          {
            value: 'actors',
            label: 'Actors',
          },
          {
            value: 'content',
            label: 'Content',
          },
        ]
      },
      languageOptions () {
        return [
          {
            value: 'en',
            label: 'English',
          },
          {
            value: 'nl',
            label: 'Nederlands',
          },
          {
            value: 'fr',
            label: 'Français',
          },
        ]
      },
      COUNTRIES () {
        return Object.keys(COUNTRIES).map(countryCode => ({
          label: COUNTRIES[countryCode],
          value: countryCode
        }))
      },
      availableSpottingAreas () {
        return this.spottingAreas.map(spottingArea => {
          return {
            label: spottingArea.name,
            value: spottingArea.id,
          }
        })
      },
      availableReportTemplates () {
        return this.reportTemplates.map(template => ({
          label: template.name,
          value: template.id
        }))
      },
      availableKnowledgeBaseCategories () {
        return this.knowledgeBaseCategories.map(category => ({
          label: category.name,
          value: category.id
        }))
      },
      availableSimplifiedFilterOptions () {
        var eligibleFilterControlOptions = [...this.filterControlOptionsActor]

        if (this.viewReportOfActorTypes && this.viewReportOfActorTypes.length > 0) {
          eligibleFilterControlOptions.push({ value: 'reports', label: 'Reports' })
        }

        return eligibleFilterControlOptions.sort((a, b) => {
          return a.label.localeCompare(b.label)
        })
      },
      visibleFilterControlOptionsActor () {
        if (!this.publisher || !this.publisher.filterControls) {
          return [{ value: 'sustainability_goal', label: 'Sustainability Goals' }]
        }

        let defaultFilters = [
          { value: 'sustainability_goal', label: 'Sustainability Goals' }
        ]

        if (this.publisher.filterControls.includes('reports')) {
          defaultFilters.push({ value: 'reports', label: 'Reports' })
        }

        return [
          ...defaultFilters,
          ...this.publisher.filterControls.map((control) => {
            return this.translatedFilterControlOptions.find(filterOption => {
              return filterOption.value === control
            })
          })
            .filter((control) => {
              if (!control) {
                return
              }

              // Only allow choosing options that are available on the simplified actors page
              return control.value !== 'sustainability_goal' && this.translatedSimplifiedFilterControlOptions.find(filterOption => {
                return filterOption.value === control.value
              })
            })]
      },
      isNew () {
        return this.$route.params.id === 'new'
      },
      mapId () {
        return this.$route.params.id || 'new'
      },
      mapUrl () {
        return this.map.embedUrl
      },
      embedCode () {
        if (this.map) {
          // For local development, use current domain if embed URL seems invalid
          let embedUrl = this.map.embedUrl

          // Check if we're in local development and fix the URL
          if (embedUrl && (embedUrl.includes('localhost') || embedUrl.includes('127.0.0.1') || !embedUrl.startsWith('http'))) {
            const currentHost = window.location.origin
            const urlParts = embedUrl.split('/ecosystem-maps/')
            if (urlParts.length > 1) {
              embedUrl = `${currentHost}/ecosystem-maps/${urlParts[1]}`
            }
          }

          return `<iframe width="100%" height="100%" src="${embedUrl}" frameborder="0" allowfullscreen></iframe>`
        }

        return ``
      },
      embedCodeScript () {
        if (this.map) {
          return `<div id="ds-interactive-map" data-api-key="${this.map.apiKey}"></div>
<script type="module" src="https://static.datascouts.eu/datascouts-visual-map-platform-connection.umd.js"><\/script>`
        }
        return ``
      },
      previewEmbedUrl () {
        if (this.map) {
          let embedUrl = this.map.embedUrl

          // For local development, always use local domain to avoid SSL issues
          if (this.isLocalDevelopment && embedUrl) {
            const currentHost = window.location.origin
            const urlParts = embedUrl.split('/ecosystem-maps/')
            if (urlParts.length > 1) {
              // Force local URL to avoid SSL certificate issues
              embedUrl = `${currentHost}/ecosystem-maps/${urlParts[1]}`
            }
          }

          return embedUrl
        }

        return null
      },
      isFormValid () {
        const noValidationErrors = Object.keys(this.validationErrors).length === 0
        const hasMapName = this.mapName.trim() !== ''

        // Recalculate hasChanges directly instead of using computed property to avoid timing issues
        const hasReportFiltersContent = this.visibleReportFilters.some(filter => filter.reportTemplateId !== null)

        let hasChanges = false
        if (!this.map) {
          hasChanges = this.mapName.trim() !== '' || this.selectedSpottingAreaId !== '' || hasReportFiltersContent
        } else {
          const mapDomains = this.map.allowedDomains || []
          const domainsChanged = JSON.stringify(this.allowedDomains.filter(d => d.trim() !== '')) !==
            JSON.stringify(mapDomains)

          const mapReportFilters = this.map.reportFieldFilters || []
          const reportFiltersChanged = JSON.stringify(this.visibleReportFilters.filter(f => f.reportTemplateId !== null)) !==
            JSON.stringify(mapReportFilters)

          hasChanges = (
            this.mapName !== this.map.name ||
            this.mapConfig.zoom !== this.map.zoom ||
            this.mapConfig.lat !== this.map.lat ||
            this.mapConfig.lng !== this.map.lng ||
            this.mapConfig.markerIcon !== this.map.markerIcon ||
            this.mapConfig.markerColor !== this.map.markerColor ||
            this.mapConfig.defaultMarkerColor !== this.map.defaultMarkerColor ||
            this.mapConfig.preferredCountry !== this.map.preferredCountry ||
            this.mapConfig.preferredConnectionsCountry !== this.map.preferredConnectionsCountry ||
            this.mapConfig.defaultLanguage !== this.map.defaultLanguage ||
            this.selectedSpottingAreaId !== this.map.spottingAreaId ||
            this.mapConfig.status !== this.map.status ||
            this.mapConfig.contentLabel !== this.map.contentLabel ||
            this.mapConfig.showEcosystemLogo !== this.map.showEcosystemLogo ||
            this.mapConfig.showAddOrganisationButton !== this.map.showAddOrganisationButton ||
            this.mapConfig.showContent !== this.map.showContent ||
            JSON.stringify(this.mapConfig.simplifiedFilterControls || []) !== JSON.stringify(this.map.simplifiedFilterControls || []) ||
            domainsChanged ||
            reportFiltersChanged
          )
        }

        return noValidationErrors &&
          hasMapName &&
          hasChanges
      },
      nameError () {
        return this.touched.name && this.validationErrors.name
      },
      zoomError () {
        return this.touched.zoom && this.validationErrors.zoom
      },
      latError () {
        return this.touched.lat && this.validationErrors.lat
      },
      lngError () {
        return this.touched.lng && this.validationErrors.lng
      },
      domainsError () {
        return this.touched.domains && this.validationErrors.domains
      },

      isLocalDevelopment () {
        return typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname.includes('.local'))
      },

      currentOrigin () {
        return typeof window !== 'undefined' ? window.location.origin : ''
      },

      hasUnsavedChanges () {
        const hasDomainsContent = this.allowedDomains.some(domain => domain.trim() !== '')
        const hasReportFiltersContent = this.visibleReportFilters.some(filter => filter.reportTemplateId !== null)
        if (!this.map) return this.mapName.trim() !== '' || this.selectedSpottingAreaId !== '' || hasDomainsContent || hasReportFiltersContent

        const mapDomains = this.map.allowedDomains || []
        const domainsChanged = JSON.stringify(this.allowedDomains.filter(d => d.trim() !== '')) !==
          JSON.stringify(mapDomains)

        const mapReportFilters = this.map.reportFieldFilters || []
        const currentFilters = this.visibleReportFilters.filter(f => f.reportTemplateId !== null)
        const reportFiltersChanged = JSON.stringify(currentFilters) !== JSON.stringify(mapReportFilters)

        return (
          this.mapName !== this.map.name ||
          this.mapConfig.zoom !== this.map.zoom ||
          this.mapConfig.lat !== this.map.lat ||
          this.mapConfig.lng !== this.map.lng ||
          this.mapConfig.markerIcon !== this.map.markerIcon ||
          this.mapConfig.markerColor !== this.map.markerColor ||
          this.mapConfig.defaultMarkerColor !== this.map.defaultMarkerColor ||
          this.mapConfig.preferredCountry !== this.map.preferredCountry ||
          this.mapConfig.preferredConnectionsCountry !== this.map.preferredConnectionsCountry ||
          this.selectedSpottingAreaId !== this.map.spottingAreaId ||
          this.mapConfig.status !== this.map.status ||
          this.mapConfig.showAddOrganisationButton !== this.map.showAddOrganisationButton ||
          JSON.stringify(this.mapConfig.simplifiedFilterControls || []) !== JSON.stringify(this.map.simplifiedFilterControls || []) ||
          domainsChanged ||
          reportFiltersChanged
        )
      },

      livePreviewEmbedUrl () {
        // For new maps, don't show preview until saved (no API key available)
        if (this.isNew || !this.map) return null

        // Use apiKey from form data (this.apiKey) as fallback
        const mapApiKey = this.map.apiKey || this.apiKey
        if (!mapApiKey) return null

        // Use the existing API key but with current form configuration as query parameters
        const baseUrl = this.previewEmbedUrl || this.map.embedUrl
        if (!baseUrl) return null

        // Parse the base URL and add current config as query params
        const url = new URL(baseUrl, window.location.origin)

        // Add current form values as override parameters - these have PRIORITY over saved config
        url.searchParams.set('zoom', this.mapConfig.zoom)
        url.searchParams.set('lat', this.mapConfig.lat)
        url.searchParams.set('lng', this.mapConfig.lng)
        url.searchParams.set('markerIcon', this.mapConfig.markerIcon)
        url.searchParams.set('markerColor', this.mapConfig.markerColor)
        url.searchParams.set('status', this.mapConfig.status)
        url.searchParams.set('showEcosystemLogo', this.mapConfig.showEcosystemLogo)

        // Add map name if provided
        if (this.mapName && this.mapName.trim()) {
          url.searchParams.set('name', this.mapName.trim())
        }

        if (this.mapConfig.preferredCountry) {
          url.searchParams.set('preferredCountry', this.mapConfig.preferredCountry)
        }
        if (this.mapConfig.preferredConnectionsCountry) {
          url.searchParams.set('preferredConnectionsCountry', this.mapConfig.preferredConnectionsCountry)
        }
        if (this.selectedSpottingAreaId) {
          url.searchParams.set('spottingAreaId', this.selectedSpottingAreaId)
        }
        if (this.mapConfig.contentLabel) {
          url.searchParams.set('contentLabel', this.mapConfig.contentLabel)
        }
        if (this.mapConfig.showAddOrganisationButton !== undefined) {
          url.searchParams.set('hideAddOrganisationButton', (!this.mapConfig.showAddOrganisationButton).toString())
        }
        if (this.mapConfig.simplifiedFilterControls?.length) {
          url.searchParams.set('simplifiedFilterControls', JSON.stringify(this.mapConfig.simplifiedFilterControls))
        }

        // Pass color assignments for live preview
        if (this.mapConfig.defaultMarkerColor) {
          url.searchParams.set('defaultMarkerColor', this.mapConfig.defaultMarkerColor)
        }

        // Pass report field filters with colors
        const filtersWithColors = this.reportFieldFiltersWithColors
        if (filtersWithColors.reportFieldFilters && filtersWithColors.reportFieldFilters.length > 0) {
          url.searchParams.set('reportFieldFilters', JSON.stringify(filtersWithColors.reportFieldFilters))
        }

        // Add a timestamp to force refresh when config changes
        url.searchParams.set('_t', Date.now())

        return url.toString()
      },

      enabledReportFieldsCount () {
        // Count the total number of enabled report field values across all filters
        let count = 0
        this.visibleReportFilters.forEach(filter => {
          if (filter.reportTemplateId) {
            filter.fields.forEach(field => {
              if (field.fieldId && field.allowedValues && field.allowedValues.length > 0) {
                count += field.allowedValues.length
              }
            })
          }
        })
        return count
      },

      enabledReportFieldValues () {
        // Get detailed information about enabled field values for color assignment
        const fieldValues = []
        this.visibleReportFilters.forEach(filter => {
          if (filter.reportTemplateId) {
            const reportName = this.reportTemplates.find(t => t.id === filter.reportTemplateId)?.name || 'Unknown Report'
            filter.fields.forEach(field => {
              if (field.fieldId && field.allowedValues && field.allowedValues.length > 0) {
                const fieldLabel = this.getFieldLabel(field.fieldId)
                field.allowedValues.forEach(allowedValue => {
                  // Find the display label for this value
                  const fieldOptions = this.getFieldOptions(field.fieldId)
                  const option = fieldOptions.find(opt => opt.value === allowedValue)
                  const valueLabel = option ? option.label : allowedValue

                  fieldValues.push({
                    reportName,
                    fieldLabel,
                    value: allowedValue,
                    valueLabel,
                    displayName: `${fieldLabel}: ${valueLabel}`
                  })
                })
              }
            })
          }
        })
        return fieldValues
      },

      reportFieldFiltersWithColors () {
        // Use the custom colors assigned per field value
        const defaultColor = this.mapConfig.defaultMarkerColor || '#3B82F6'

        // Create report field filters with color assignments
        const filtersWithColors = this.visibleReportFilters
          .filter(filter => filter.reportTemplateId !== null)
          .map(filter => {
            const filterWithColors = {
              reportTemplateId: filter.reportTemplateId,
              fields: filter.fields.map(field => {
                if (field.fieldId && field.allowedValues && field.allowedValues.length > 0) {
                  // Create field values with their custom assigned colors
                  const fieldValuesWithColors = field.allowedValues.map((allowedValue) => {
                    // Use the custom color from colorAssignments, or use default
                    const assignedColor = field.colorAssignments?.[allowedValue] || defaultColor

                    return {
                      value: allowedValue,
                      color: assignedColor
                    }
                  })

                  return {
                    fieldId: field.fieldId,
                    allowedValues: field.allowedValues, // Keep original structure for compatibility
                    colorAssignments: fieldValuesWithColors // Add color assignments
                  }
                }
                return field
              })
            }
            return filterWithColors
          })

        return {
          defaultMarkerColor: defaultColor,
          reportFieldFilters: filtersWithColors
        }
      },
      allCategoriesSelected () {
        return this.isTaxonomyAllSelected('categories')
      },
      allActivitiesSelected () {
        return this.isTaxonomyAllSelected('activities')
      },
      allDomainsSelected () {
        return this.isTaxonomyAllSelected('domains')
      },
      allTechnologiesSelected () {
        return this.isTaxonomyAllSelected('technologies')
      },
      allIndustriesSelected () {
        return this.isTaxonomyAllSelected('industries')
      },
      allMembershipsSelected () {
        return this.isTaxonomyAllSelected('memberships')
      },
      allStagesSelected () {
        return this.isTaxonomyAllSelected('stages')
      },
      allFunnelStagesSelected () {
        return this.isTaxonomyAllSelected('funnelStages')
      },
      allExpertisesSelected () {
        return this.isTaxonomyAllSelected('expertises')
      },
      allMotivationsSelected () {
        return this.isTaxonomyAllSelected('motivations')
      },
      allReadinessLevelsSelected () {
        return this.isTaxonomyAllSelected('readinessLevels')
      },
      allProcessStepsSelected () {
        return this.isTaxonomyAllSelected('processSteps')
      },
      allBusinessAspectsSelected () {
        return this.isTaxonomyAllSelected('businessAspects')
      },
      allProductFeaturesASelected () {
        return this.isTaxonomyAllSelected('productFeaturesA')
      },
      allProductFeaturesBSelected () {
        return this.isTaxonomyAllSelected('productFeaturesB')
      },
      allProductFeaturesCSelected () {
        return this.isTaxonomyAllSelected('productFeaturesC')
      },
      // Get taxonomy labels from ecosystem config
      taxonomyLabels () {
        return {
          categories: window.config?.categoriesAlias || 'Categories',
          activities: window.config?.activitiesAlias || 'Activities',
          domains: window.config?.domainsAlias || 'Domains',
          technologies: window.config?.technologiesAlias || 'Technologies',
          industries: window.config?.industriesAlias || 'Industries',
          memberships: window.config?.membershipsAlias || 'Memberships',
          stages: window.config?.stagesAlias || 'Stages',
          funnelStages: window.config?.funnelStagesAlias || 'Funnel Stages',
          expertises: window.config?.expertisesAlias || 'Expertises',
          motivations: window.config?.motivationsAlias || 'Motivations',
          readinessLevels: window.config?.readinessLevelsAlias || 'Readiness Levels',
          processSteps: window.config?.processStepsAlias || 'Process Steps',
          businessAspects: window.config?.businessAspectsAlias || 'Business Aspects',
          productFeaturesA: window.config?.productFeaturesAAlias || 'Product Features A',
          productFeaturesB: window.config?.productFeaturesBAlias || 'Product Features B',
          productFeaturesC: window.config?.productFeaturesCAlias || 'Product Features C',
        }
      },
      // List of all taxonomy keys for iteration
      allTaxonomyKeys () {
        return [
          'categories', 'activities', 'domains', 'technologies', 'industries',
          'memberships', 'stages', 'funnelStages', 'expertises', 'motivations',
          'readinessLevels', 'processSteps', 'businessAspects',
          'productFeaturesA', 'productFeaturesB', 'productFeaturesC'
        ]
      },
    },
    async mounted () {
      // Load taxonomies
      await this.loadTaxonomies()
      // Load report templates and knowledge base categories first
      await this.loadReportTemplates()
      await this.loadKnowledgeBaseCategories()

      if (!this.isNew) {
        await this.loadMapData()
      } else {
        this.apiKey = this.generateApiKey()
        this.loading = false
      }
    },
    watch: {
      // Watch for changes in form values and refresh preview
      mapConfig: {
        handler (newConfig, oldConfig) {
          // For immediate visual feedback (dropdowns, sliders, etc.), refresh immediately
          // For text inputs that might be part of mapConfig, we'd need to be more specific
          this.$nextTick(() => {
            this.refreshPreview()
          })
        },
        deep: true
      },
      selectedSpottingAreaId () {
        // Spotting area changes should refresh immediately (dropdown selection)
        this.$nextTick(() => {
          this.refreshPreview()
        })
      },
      mapName () {
        // Map name changes should be debounced (text input)
        this.$nextTick(() => {
          this.debouncedRefreshPreview()
        })
      },
      visibleReportFilters: {
        handler (newFilters, oldFilters) {
          this.$forceUpdate()
          // Debounce preview refresh to avoid "Too many requests" errors
          this.$nextTick(() => {
            this.debouncedRefreshPreview()
          })
        },
        deep: true
      }
    },
    methods: {
      updateDefaultTab (value) {
        this.mapConfig.defaultTab = value
      },
      updatePreferredCountry (value) {
        this.mapConfig.preferredCountry = value
      },
      updatePreferredConnectionsCountry (value) {
        this.mapConfig.preferredConnectionsCountry = value
      },
      updateMarkerColor: debounce(function (event) {
        this.mapConfig.markerColor = event
      }, 1000),
      updateDefaultMarkerColor (color) {
        this.mapConfig.markerColor = color
      },
      generateAllFieldColors (colors) {
        // Apply the generated colors to all field values
        let colorIndex = 0

        this.visibleReportFilters.forEach(filter => {
          if (filter.reportTemplateId) {
            filter.fields.forEach(field => {
              if (field.fieldId && field.allowedValues && field.allowedValues.length > 0) {
                // Initialize colorAssignments if needed
                if (!field.colorAssignments) {
                  field.colorAssignments = {}
                }

                // Assign colors to each allowed value
                field.allowedValues.forEach(allowedValue => {
                  if (colorIndex < colors.length) {
                    field.colorAssignments[allowedValue] = colors[colorIndex]
                    colorIndex++
                  }
                })
              }
            })
          }
        })

        // Force reactivity update
        this.visibleReportFilters = [...this.visibleReportFilters]
        this.$forceUpdate()
      },
      goBack () {
        this.$router.push('/settings/ecosystem-maps')
      },
      async handleSave () {
        this.saving = true
        // Validate form before saving
        if (!this.validateForm()) {
          console.error('Form validation failed')
          this.saving = false
          return
        }

        try {
          // Get color assignments for report field filters
          const colorData = this.reportFieldFiltersWithColors

          const mapData = {
            name: this.mapName,
            apiKey: this.apiKey,
            zoom: parseInt(this.mapConfig.zoom),
            lat: parseFloat(this.mapConfig.lat),
            lng: parseFloat(this.mapConfig.lng),
            markerIcon: this.mapConfig.markerIcon,
            markerColor: this.mapConfig.markerColor,
            preferredCountry: this.mapConfig.preferredCountry,
            preferredConnectionsCountry: this.mapConfig.preferredConnectionsCountry,
            defaultLanguage: this.mapConfig.defaultLanguage,
            spottingAreaId: this.selectedSpottingAreaId || null,
            status: this.mapConfig.status,
            showAddOrganisationButton: this.mapConfig.showAddOrganisationButton,
            showContent: this.mapConfig.showContent,
            simplifiedFilterControls: this.mapConfig.simplifiedFilterControls || [],
            allowedDomains: this.allowedDomains.filter(domain => domain.trim() !== ''),
            filterType: this.filterType,
            reportFieldFilters: colorData.reportFieldFilters,
            taxonomyFilters: this.buildTaxonomyFiltersForSave(),
            contentLabel: this.mapConfig.contentLabel,
            showEcosystemLogo: this.mapConfig.showEcosystemLogo,
            defaultTab: this.mapConfig.defaultTab
          }

          if (this.isNew) {
            const response = await EcosystemMapsApi.createEcosystemMap(mapData)
            // Direct assignment works in Vue 3 with reactive data
            this.saving = true

            this.map = response.map
            // Redirect to edit mode after creating
            this.$router.push(`/settings/ecosystem-maps/${this.map.id}`).then(() => {
              // Refresh the map in some sort a way
              this.$nextTick(() => {
                this.livePreviewKey++
                window.location.reload()
              })
            })
          } else {
            const response = await EcosystemMapsApi.updateEcosystemMap(this.mapId, mapData)
            // Direct assignment works in Vue 3 with reactive data
            this.map = response
            // Force iframe re-render after save by incrementing key
            this.$nextTick(() => {
              this.livePreviewKey++
            })

            this.saving = false
          }
        } catch (error) {
          console.error('Failed to save map:', error)
          console.error('Failed to save map configuration')
        } finally {
          if (!this.isNew) {
            this.saving = false
          }
        }
      },

      async loadMapData () {
        try {
          this.loading = true
          this.error = null
          const response = await EcosystemMapsApi.fetchEcosystemMap(this.mapId)
          this.map = response
          this.loadMapFromData(this.map)
        } catch (error) {
          console.error('Failed to load map data:', error)
          this.error = 'Failed to load map data'
          console.error('Failed to load map data')
        } finally {
          this.loading = false
        }
      },

      loadMapFromData (map) {
        this.mapName = map.name
        this.apiKey = map.apiKey
        this.mapConfig = {
          zoom: parseInt(map.zoom) || 4,
          lat: parseFloat(map.lat) || 50.0,
          lng: parseFloat(map.lng) || 15.0,
          markerIcon: map.markerIcon || 'default',
          markerColor: map.markerColor || '#3B82F6',
          preferredCountry: map.preferredCountry || null,
          preferredConnectionsCountry: map.preferredConnectionsCountry || null,
          defaultLanguage: map.defaultLanguage || 'en',
          status: map.status || 'draft',
          showAddOrganisationButton: map.showAddOrganisationButton !== undefined ? map.showAddOrganisationButton : true,
          showContent: map.showContent !== undefined ? map.showContent : false,
          simplifiedFilterControls: map.simplifiedFilterControls || [],
          contentLabel: map.contentLabel || 'landscapes',
          showEcosystemLogo: map.showEcosystemLogo || true,
          defaultTab: map.defaultTab || 'actors',
        }

        // Set the theme color for the ColorThemeSelector from the saved default marker color
        this.$nextTick(() => {
          if (this.$refs.colorThemeSelector && map.markerColor) {
            this.$refs.colorThemeSelector.selectedColor = map.markerColor
          }
        })
        this.selectedSpottingAreaId = map.spottingAreaId || ''
        // Load allowed domains or default to one empty field
        this.allowedDomains = map.allowedDomains && map.allowedDomains.length > 0
          ? [...map.allowedDomains]
          : ['']

        // Load filter type
        this.filterType = map.filterType || 'report_fields'

        // Load taxonomy filters if they exist
        if (map.taxonomyFilters) {
          this.restoreTaxonomyFilters(map.taxonomyFilters)
        }

        // Load report field filters or default to one empty filter
        if (map.reportFieldFilters && map.reportFieldFilters.length > 0) {
          // Deep clone and convert colorAssignments from array to object format
          this.visibleReportFilters = JSON.parse(JSON.stringify(map.reportFieldFilters)).map(filter => ({
            ...filter,
            fields: filter.fields.map(field => {
              // Convert colorAssignments from array [{value, color}] to object {value: color}
              const colorAssignmentsObj = {}
              if (Array.isArray(field.colorAssignments)) {
                field.colorAssignments.forEach(assignment => {
                  colorAssignmentsObj[assignment.value] = assignment.color
                })
              }
              return {
                ...field,
                colorAssignments: colorAssignmentsObj
              }
            })
          }))
        } else {
          this.visibleReportFilters = [{ reportTemplateId: null, fields: [{ fieldId: null, allowedValues: [], colorAssignments: {} }] }]
        }

        // If we have color assignments from the database, we'll need to restore them to the ColorThemeSelector
        // This will happen after the component is mounted and the color selector is available

        // Load field options and template fields for existing filters
        this.visibleReportFilters.forEach(filter => {
          if (filter.reportTemplateId && !this.reportFields[filter.reportTemplateId]) {
            this.loadReportTemplateFields(filter.reportTemplateId)
          }
          filter.fields.forEach(field => {
            if (field.fieldId && !this.reportFieldOptions[field.fieldId]) {
              this.loadFieldOptions(field.fieldId)
            }
          })
        })
      },
      generateApiKey () {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
        let result = 'ds_live_'
        for (let i = 0; i < 20; i++) {
          result += chars.charAt(Math.floor(Math.random() * chars.length))
        }
        return result
      },
      copyApiKey () {
        navigator.clipboard.writeText(this.apiKey).then(() => {
          console.log('API key copied to clipboard!')
        }).catch(() => {
          console.error('Failed to copy to clipboard')
        })
      },
      copyEmbedCode () {
        navigator.clipboard.writeText(this.embedCode).then(() => {
          console.log('Embed code copied to clipboard!')
        }).catch(() => {
          console.error('Failed to copy to clipboard')
        })
      },
      copyEmbedCodeScript () {
        navigator.clipboard.writeText(this.embedCodeScript).then(() => {
          console.log('JavaScript embed code copied to clipboard!')
        }).catch(() => {
          console.error('Failed to copy to clipboard')
        })
      },

      validateName () {
        this.touched.name = true
        if (!this.mapName || this.mapName.trim() === '') {
          this.validationErrors.name = 'Map name is required'
        } else if (this.mapName.trim().length < 3) {
          this.validationErrors.name = 'Map name must be at least 3 characters'
        } else if (this.mapName.trim().length > 100) {
          this.validationErrors.name = 'Map name must be less than 100 characters'
        } else {
          delete this.validationErrors.name
        }
      },
      validateZoom () {
        this.touched.zoom = true
        const zoom = parseInt(this.mapConfig.zoom)
        if (isNaN(zoom) || zoom < 1 || zoom > 18) {
          this.validationErrors.zoom = 'Zoom level must be between 1 and 18'
        } else {
          delete this.validationErrors.zoom
        }
      },
      validateLat () {
        this.touched.lat = true
        const lat = parseFloat(this.mapConfig.lat)
        if (isNaN(lat) || lat < -90 || lat > 90) {
          this.validationErrors.lat = 'Latitude must be between -90 and 90'
        } else {
          delete this.validationErrors.lat
        }
      },
      validateLng () {
        this.touched.lng = true
        const lng = parseFloat(this.mapConfig.lng)
        if (isNaN(lng) || lng < -180 || lng > 180) {
          this.validationErrors.lng = 'Longitude must be between -180 and 180'
        } else {
          delete this.validationErrors.lng
        }
      },
      validateSingleDomain (domain) {
        if (!domain || domain.trim() === '') {
          return null // Empty domains are allowed (will be filtered out)
        }

        domain = domain.trim()

        if (domain.includes('://')) {
          return 'Don\'t include http:// or https://'
        } else if (domain.includes(' ')) {
          return 'Domain cannot contain spaces'
        } else if (domain.startsWith('.') || domain.endsWith('.')) {
          return 'Domain cannot start or end with a dot'
        } else if (!domain.includes('.')) {
          return 'Please enter a valid domain (e.g., example.com)'
        } else if (!/^[a-zA-Z0-9.-]+$/.test(domain)) {
          return 'Domain can only contain letters, numbers, dots, and hyphens'
        } else if (domain.length > 253) {
          return 'Domain name is too long'
        } else {
          // Additional validation for common mistakes
          const parts = domain.split('.')
          if (parts.some(part => part.length === 0)) {
            return 'Invalid domain format'
          } else if (parts.some(part => part.startsWith('-') || part.endsWith('-'))) {
            return 'Domain parts cannot start or end with hyphens'
          }
        }

        return null
      },
      validateDomains () {
        this.touched.domains = true
        const validDomains = this.allowedDomains.filter(domain => domain.trim() !== '')

        // Check for duplicates
        const uniqueDomains = new Set(validDomains.map(d => d.trim().toLowerCase()))
        if (uniqueDomains.size !== validDomains.length) {
          this.validationErrors.domains = 'Duplicate domains are not allowed'
          return
        }

        // Validate each domain
        for (const domain of validDomains) {
          const error = this.validateSingleDomain(domain)
          if (error) {
            this.validationErrors.domains = error
            return
          }
        }

        delete this.validationErrors.domains
      },
      getDomainError (index) {
        if (!this.touched.domains || !this.validationErrors.domains) return false
        return this.allowedDomains[index] && this.allowedDomains[index].trim() !== ''
      },
      addDomain () {
        this.allowedDomains.push('')
      },
      removeDomain (index) {
        this.allowedDomains.splice(index, 1)
        this.validateDomains()
      },
      validateForm () {
        this.validateName()
        this.validateZoom()
        this.validateLat()
        this.validateLng()
        this.validateDomains()
        return this.isFormValid
      },

      openMapModal () {
        // Copy current config to temp config
        this.tempMapConfig = {
          zoom: this.mapConfig.zoom,
          lat: this.mapConfig.lat,
          lng: this.mapConfig.lng
        }
        this.showMapModal = true

        // Initialize map after modal is shown
        this.$nextTick(() => {
          this.initializeLeafletMap()
        })
      },

      closeMapModal () {
        this.showMapModal = false
        if (this.leafletMap) {
          this.leafletMap.remove()
          this.leafletMap = null
        }
        // Clean up city search
        this.citySearchQuery = ''
        this.citySearchResults = []
        this.cityMarker = null
      },

      applyMapSettings () {
        // Apply temp config to actual config
        this.mapConfig.zoom = this.tempMapConfig.zoom
        this.mapConfig.lat = this.tempMapConfig.lat
        this.mapConfig.lng = this.tempMapConfig.lng
        this.closeMapModal()
      },

      async searchCityInModal () {
        if (!this.citySearchQuery.trim()) {
          return
        }

        this.searchingCity = true
        this.citySearchResults = []
        try {
          // Use internal GeoNames API
          const query = encodeURIComponent(this.citySearchQuery.trim())
          const response = await fetch(`/api/geo/search-city?city=${query}`)

          if (!response.ok) {
            throw new Error('City search failed')
          }

          const data = await response.json()

          if (data.cities && data.cities.length > 0) {
            this.citySearchResults = data.cities
          } else {
            this.$notify({
              type: 'warning',
              title: 'No Results',
              text: `City "${this.citySearchQuery}" not found`
            })
          }
        } catch (error) {
          console.error('City search error:', error)
          this.$notify({
            type: 'error',
            title: 'Search Failed',
            text: 'Failed to search for city. Please try again.'
          })
        } finally {
          this.searchingCity = false
        }
      },

      async selectCity (city) {
        // Update temp config
        this.tempMapConfig.lat = parseFloat(city.lat)
        this.tempMapConfig.lng = parseFloat(city.lng)

        // Move map to the location
        if (this.leafletMap) {
          this.leafletMap.setView([city.lat, city.lng], 10)

          // Remove existing city marker if any
          if (this.cityMarker) {
            this.leafletMap.removeLayer(this.cityMarker)
          }

          // Add marker for the city
          const L = await import('leaflet')
          this.cityMarker = L.default.marker([city.lat, city.lng], {
            icon: L.default.icon({
              iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
              shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
              iconSize: [25, 41],
              iconAnchor: [12, 41],
              popupAnchor: [1, -34],
              shadowSize: [41, 41]
            })
          }).addTo(this.leafletMap)

          this.cityMarker.bindPopup(city.display_name).openPopup()
        }

        // Notify success
        this.$notify({
          type: 'success',
          title: 'City Found',
          text: `Centered map on ${city.display_name}`
        })

        // Clear search query and results
        this.citySearchQuery = ''
        this.citySearchResults = []
      },

      async initializeLeafletMap () {
        try {
          // Dynamically import Leaflet
          const L = await import('leaflet')

          // Import Leaflet CSS
          if (!document.querySelector('link[href*="leaflet"]')) {
            const link = document.createElement('link')
            link.rel = 'stylesheet'
            link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'
            document.head.appendChild(link)
          }

          // Fix default marker icon paths
          delete L.Icon.Default.prototype._getIconUrl
          L.Icon.Default.mergeOptions({
            iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
            iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
            shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
          })

          // Initialize map
          this.leafletMap = L.map('map-selector').setView(
            [this.tempMapConfig.lat, this.tempMapConfig.lng],
            this.tempMapConfig.zoom
          )

          // Add tile layer
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors'
          }).addTo(this.leafletMap)

          // Add center marker
          let centerMarker = L.marker([this.tempMapConfig.lat, this.tempMapConfig.lng])
            .addTo(this.leafletMap)

          // Update config when map moves
          this.leafletMap.on('moveend', () => {
            const center = this.leafletMap.getCenter()
            this.tempMapConfig.lat = center.lat
            this.tempMapConfig.lng = center.lng
            this.tempMapConfig.zoom = this.leafletMap.getZoom()

            // Update marker position
            centerMarker.setLatLng([center.lat, center.lng])
          })

          // Update config when zoom changes
          this.leafletMap.on('zoomend', () => {
            this.tempMapConfig.zoom = this.leafletMap.getZoom()
          })

          // Click to set center
          this.leafletMap.on('click', (e) => {
            this.leafletMap.setView([e.latlng.lat, e.latlng.lng])
            centerMarker.setLatLng([e.latlng.lat, e.latlng.lng])
          })

        } catch (error) {
          console.error('Failed to load Leaflet:', error)
          console.error('Failed to load map. Please check your internet connection.')
        }
      },

      handleIframeError () {
        this.iframeError = true
        // Check if it's a CORS error by trying to access iframe properties
        try {
          if (this.$refs.mapContainer) {
            const iframe = this.$refs.mapContainer.querySelector('iframe')
            if (iframe && iframe.contentWindow === null) {
              this.corsError = true
            }
          }
        } catch (error) {
          this.corsError = true
        }
        console.error('Iframe failed to load, likely due to SSL certificate or CORS issues')
      },

      async tryDirectApi () {
        if (!this.map || !this.map.apiKey) return

        try {
          console.log('Testing direct API access...')
          const response = await EcosystemMapsApi.fetchEcosystemMapEmbedData(this.map.id, this.map.apiKey)
          console.log('Direct API works!', response)
          alert('Direct API access successful! Check console for details.')
        } catch (error) {
          console.error('Direct API failed:', error)
          alert('Direct API also failed. This confirms CORS/SSL issues.')
        }
      },

      getCountryName (countryCode) {
        if (!countryCode) return ''
        const country = this.COUNTRIES.find(c => c.value === countryCode)
        return country ? country.label : countryCode
      },

      getSpottingAreaName (spottingAreaId) {
        if (!spottingAreaId) return ''
        const area = this.availableSpottingAreas.find(a => a.value === spottingAreaId)
        return area ? area.label : `Area ${spottingAreaId}`
      },

      getStatusClass (status) {
        const statusClasses = {
          active: 'text-green-600 font-medium',
          inactive: 'text-gray-500',
          draft: 'text-yellow-600 font-medium'
        }
        return statusClasses[status] || 'text-gray-500'
      },

      handleIframeLoad () {
        this.iframeLoading = false
        this.iframeError = false
      },

      retryEmbed () {
        this.iframeError = false
        this.iframeLoading = true
        this.livePreviewKey++
      },

      refreshPreview () {
        if (this.livePreviewEmbedUrl) {
          this.iframeLoading = true
          this.livePreviewKey++
        }
      },

      debouncedRefreshPreview () {
        // Clear existing timer
        if (this.refreshDebounceTimer) {
          clearTimeout(this.refreshDebounceTimer)
        }

        // Set new timer - wait 1500ms after user stops making changes to avoid rate limiting
        this.refreshDebounceTimer = setTimeout(() => {
          this.refreshPreview()
        }, 1500)
      },

      // Report Filter Management
      addReportFilter () {
        this.visibleReportFilters.push({
          reportTemplateId: null,
          fields: [{ fieldId: null, allowedValues: [], colorAssignments: {} }]
        })
      },
      removeReportFilter (index) {
        this.visibleReportFilters.splice(index, 1)
      },
      clearReportTemplate (index) {
        this.visibleReportFilters[index].reportTemplateId = null
        this.visibleReportFilters[index].fields = []

        // Force reactivity update by creating a new array reference
        this.visibleReportFilters = [...this.visibleReportFilters]
      },
      onReportTemplateChange (index, templateId) {
        this.visibleReportFilters[index].reportTemplateId = templateId
        this.visibleReportFilters[index].fields = [{ fieldId: null, allowedValues: [], colorAssignments: {} }]

        // Force reactivity update by creating a new array reference
        this.visibleReportFilters = [...this.visibleReportFilters]

        // Load fields for this template if not already loaded
        if (templateId && !this.reportFields[templateId]) {
          this.loadReportTemplateFields(templateId)
        }
      },
      addFieldToReport (reportIndex) {
        this.visibleReportFilters[reportIndex].fields.push({ fieldId: null, allowedValues: [], colorAssignments: {} })
      },
      removeField (reportIndex, fieldIndex) {
        this.visibleReportFilters[reportIndex].fields.splice(fieldIndex, 1)
      },
      onFieldChange (reportIndex, fieldIndex, fieldId) {
        // Update field ID and reset allowed values
        this.visibleReportFilters[reportIndex].fields[fieldIndex].fieldId = fieldId
        this.visibleReportFilters[reportIndex].fields[fieldIndex].allowedValues = []
        this.visibleReportFilters[reportIndex].fields[fieldIndex].colorAssignments = {}

        // Force reactivity update by creating a new array reference
        this.visibleReportFilters = [...this.visibleReportFilters]

        // Always load field options when field changes
        if (fieldId) {
          this.loadFieldOptions(fieldId)
        }
      },
      onFieldValueToggle (reportIndex, fieldIndex, value) {
        const fieldConfig = this.visibleReportFilters[reportIndex].fields[fieldIndex]

        // Initialize colorAssignments if not exists
        if (!fieldConfig.colorAssignments) {
          fieldConfig.colorAssignments = {}
        }

        // If the value was just checked (added to allowedValues), assign a default color
        if (fieldConfig.allowedValues.includes(value) && !fieldConfig.colorAssignments[value]) {
          fieldConfig.colorAssignments[value] = this.generateDistinctColor(reportIndex, fieldIndex)
        }

        // Force reactivity update
        this.$forceUpdate()
      },
      onFieldValueColorChange (reportIndex, fieldIndex, value) {
        // Color is already bound via v-model
        // The visibleReportFilters watcher will handle debounced preview refresh
      },
      generateDistinctColor (reportIndex, fieldIndex) {
        // Generate a distinct color based on how many colors are already assigned
        const allColors = []
        this.visibleReportFilters.forEach(filter => {
          filter.fields.forEach(field => {
            if (field.colorAssignments) {
              allColors.push(...Object.values(field.colorAssignments))
            }
          })
        })

        // Use a simple hue rotation to generate distinct colors
        const hueStep = 360 / Math.max(12, allColors.length + 1) // At least 12 distinct hues
        const hue = (allColors.length * hueStep) % 360

        // Convert HSL to hex
        return this.hslToHex(hue, 70, 50)
      },
      hslToHex (h, s, l) {
        s /= 100
        l /= 100
        const c = (1 - Math.abs(2 * l - 1)) * s
        const x = c * (1 - Math.abs((h / 60) % 2 - 1))
        const m = l - c / 2
        let r = 0, g = 0, b = 0

        if (0 <= h && h < 60) {
          r = c;
          g = x;
          b = 0
        } else if (60 <= h && h < 120) {
          r = x;
          g = c;
          b = 0
        } else if (120 <= h && h < 180) {
          r = 0;
          g = c;
          b = x
        } else if (180 <= h && h < 240) {
          r = 0;
          g = x;
          b = c
        } else if (240 <= h && h < 300) {
          r = x;
          g = 0;
          b = c
        } else if (300 <= h && h < 360) {
          r = c;
          g = 0;
          b = x
        }

        r = Math.round((r + m) * 255)
        g = Math.round((g + m) * 255)
        b = Math.round((b + m) * 255)

        return '#' + [r, g, b].map(x => {
          const hex = x.toString(16)
          return hex.length === 1 ? '0' + hex : hex
        }).join('')
      },
      getAvailableFieldsForTemplate (templateId) {
        return this.reportFields[templateId] || []
      },
      getAvailableFieldsForTemplateExcludingSelected (templateId, reportIndex, currentFieldIndex) {
        const allFields = this.reportFields[templateId] || []

        // Get already selected field IDs for this report template
        const selectedFieldIds = this.visibleReportFilters[reportIndex]?.fields
          ?.map((field, index) => index !== currentFieldIndex ? field.fieldId : null)
          ?.filter(fieldId => fieldId !== null) || []

        // Filter out already selected fields
        return allFields.filter(field => !selectedFieldIds.includes(field.id))
      },
      getFieldLabel (fieldId) {
        // Find field label across all templates
        for (const templateId in this.reportFields) {
          const field = this.reportFields[templateId].find(f => f.id === fieldId)
          if (field) return field.label
        }
        return `Field ${fieldId}`
      },
      getFieldOptions (fieldId) {
        return this.reportFieldOptions[fieldId] || []
      },
      toggleAllFieldValues (reportIndex, fieldIndex) {
        const fieldConfig = this.visibleReportFilters[reportIndex].fields[fieldIndex]
        const allOptions = this.getFieldOptions(fieldConfig.fieldId)

        if (fieldConfig.allowedValues.length === allOptions.length) {
          // Deselect all
          fieldConfig.allowedValues = []
        } else {
          // Select all
          fieldConfig.allowedValues = allOptions.map(option => option.value)
        }
      },

      // Taxonomy Filter Management
      async loadTaxonomies () {
        try {
          // Helper function to get hex color from taxonomy
          const getHexColor = (taxonomy) => {
            const hexColours = window.config?.hexColours || {}
            return hexColours[taxonomy.hex]
          }

          // Helper function to map flat taxonomy (no children)
          const mapFlatTaxonomy = (tax) => ({
            id: tax.id,
            name: tax.name,
            hex: getHexColor(tax),
            selected: false,
            expanded: false,
            children: []
          })

          // Helper function to map hierarchical taxonomy with custom children property
          const mapHierarchicalTaxonomy = (tax, childrenProp) => ({
            id: tax.id,
            name: tax.name,
            hex: getHexColor(tax),
            selected: false,
            expanded: false,
            children: (tax[childrenProp] || []).map(child => ({
              id: child.id,
              name: child.name,
              hex: getHexColor(child),
              selected: false
            }))
          })

          // Load from window.ALL_TAXONOMIES instead of API
          const allTaxonomies = window.ALL_TAXONOMIES || {}

          // Update all at once to ensure reactivity
          this.taxonomyFilters = {
            // Flat taxonomies
            categories: (allTaxonomies.categories || []).map(mapFlatTaxonomy),
            activities: (allTaxonomies.activities || []).map(mapFlatTaxonomy),
            memberships: (allTaxonomies.memberships || []).map(mapFlatTaxonomy),
            stages: (allTaxonomies.stages || []).map(mapFlatTaxonomy),
            funnelStages: (allTaxonomies.funnelStages || []).map(mapFlatTaxonomy),
            expertises: (allTaxonomies.expertises || []).map(mapFlatTaxonomy),
            motivations: (allTaxonomies.motivations || []).map(mapFlatTaxonomy),
            readinessLevels: (allTaxonomies.readinessLevels || []).map(mapFlatTaxonomy),
            processSteps: (allTaxonomies.processSteps || []).map(mapFlatTaxonomy),
            businessAspects: (allTaxonomies.businessAspects || []).map(mapFlatTaxonomy),
            // Hierarchical taxonomies
            domains: (allTaxonomies.domains || []).map(tax => mapHierarchicalTaxonomy(tax, 'domains')),
            industries: (allTaxonomies.industries || []).map(tax => mapHierarchicalTaxonomy(tax, 'industries')),
            technologies: (allTaxonomies.technologies || []).map(tax => mapHierarchicalTaxonomy(tax, 'children')),
            productFeaturesA: (allTaxonomies.productFeaturesA || []).map(tax => mapHierarchicalTaxonomy(tax, 'children')),
            productFeaturesB: (allTaxonomies.productFeaturesB || []).map(tax => mapHierarchicalTaxonomy(tax, 'children')),
            productFeaturesC: (allTaxonomies.productFeaturesC || []).map(tax => mapHierarchicalTaxonomy(tax, 'children')),
          }

          // Force Vue to update
          this.$forceUpdate()
        } catch (error) {
          console.error('Error loading taxonomies:', error)
          this.$store.dispatch('NOTIFICATIONS/showError', { message: 'Failed to load taxonomies' })
        }
      },

      // Helper to restore taxonomy filters from saved data
      restoreTaxonomyFilters (savedFilters) {
        const taxonomyKeys = this.allTaxonomyKeys

        taxonomyKeys.forEach(key => {
          if (savedFilters[key]) {
            savedFilters[key].forEach(savedItem => {
              const item = this.taxonomyFilters[key]?.find(i => i.id === savedItem.id)
              if (item) {
                item.selected = savedItem.selected !== false
                item.hex = savedItem.hex || item.hex
                // Restore children selection
                if (savedItem.children && savedItem.children.length > 0 && item.children) {
                  item.expanded = true // Auto-expand if has selected children
                  savedItem.children.forEach(savedChild => {
                    const child = item.children.find(c => c.id === savedChild.id)
                    if (child) {
                      child.selected = true
                    }
                  })
                }
              }
            })
          }
        })
      },

      // Helper to build taxonomy filters for saving
      buildTaxonomyFiltersForSave () {
        const result = {}
        const taxonomyKeys = this.allTaxonomyKeys

        taxonomyKeys.forEach(key => {
          const items = this.taxonomyFilters[key] || []
          result[key] = items
            .filter(item => item.selected || (item.children && item.children.some(child => child.selected)))
            .map(item => ({
              id: item.id,
              name: item.name,
              hex: item.hex,
              selected: item.selected,
              children: (item.children || [])
                .filter(child => child.selected)
                .map(child => ({ id: child.id, name: child.name }))
            }))
        })

        return result
      },

      // Generic helper to check if all items in a taxonomy are selected
      isTaxonomyAllSelected (taxonomyKey) {
        const items = this.taxonomyFilters[taxonomyKey] || []
        return items.length > 0 && items.every(item => item.selected)
      },

      // Generic toggle all for any taxonomy
      toggleAllTaxonomy (taxonomyKey) {
        const allSelected = this.isTaxonomyAllSelected(taxonomyKey)
        const items = this.taxonomyFilters[taxonomyKey] || []
        items.forEach(item => {
          item.selected = !allSelected
        })
      },

      toggleAllCategories () {
        this.toggleAllTaxonomy('categories')
      },

      toggleAllActivities () {
        this.toggleAllTaxonomy('activities')
      },

      toggleAllDomains () {
        this.toggleAllTaxonomy('domains')
      },

      toggleAllTechnologies () {
        this.toggleAllTaxonomy('technologies')
      },

      toggleAllIndustries () {
        this.toggleAllTaxonomy('industries')
      },

      toggleAllMemberships () {
        this.toggleAllTaxonomy('memberships')
      },

      toggleAllStages () {
        this.toggleAllTaxonomy('stages')
      },

      toggleAllFunnelStages () {
        this.toggleAllTaxonomy('funnelStages')
      },

      toggleAllExpertises () {
        this.toggleAllTaxonomy('expertises')
      },

      toggleAllMotivations () {
        this.toggleAllTaxonomy('motivations')
      },

      toggleAllReadinessLevels () {
        this.toggleAllTaxonomy('readinessLevels')
      },

      toggleAllProcessSteps () {
        this.toggleAllTaxonomy('processSteps')
      },

      toggleAllBusinessAspects () {
        this.toggleAllTaxonomy('businessAspects')
      },

      toggleAllProductFeaturesA () {
        this.toggleAllTaxonomy('productFeaturesA')
      },

      toggleAllProductFeaturesB () {
        this.toggleAllTaxonomy('productFeaturesB')
      },

      toggleAllProductFeaturesC () {
        this.toggleAllTaxonomy('productFeaturesC')
      },

      generateDistinctColorForTaxonomy () {
        // Simple color generation - can be enhanced
        const colors = [
          '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
          '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16'
        ]
        // Collect used colors from all taxonomies
        const usedColors = this.allTaxonomyKeys
          .flatMap(key => (this.taxonomyFilters[key] || []).map(item => item.hex))
          .filter(Boolean)

        // Find first unused color
        const unusedColor = colors.find(color => !usedColors.includes(color))
        return unusedColor || colors[0]
      },

      async loadReportTemplates () {
        try {
          const reports = await EcosystemReports.get()
          this.reportTemplates = reports || []

          // Pre-load report fields for each template
          this.reportFields = {}
          reports.forEach(report => {
            if (report.reportFields) {
              this.reportFields[report.id] = report.reportFields.filter(field =>
                field.can_be_used_as_filter &&
                ['options', 'multi_select', 'checkbox'].includes(field.type)
              )
            }
          })
        } catch (error) {
          console.error('Failed to load report templates:', error)
          this.reportTemplates = []
        }
      },
      async loadKnowledgeBaseCategories () {
        try {
          const response = await fetch('/api/knowledge-base/categories')
          if (!response.ok) {
            throw new Error('Failed to fetch knowledge base categories')
          }
          const data = await response.json()
          this.knowledgeBaseCategories = data.data || []
        } catch (error) {
          console.error('Failed to load knowledge base categories:', error)
          this.knowledgeBaseCategories = []
        }
      },
      async loadReportTemplateFields (templateId) {
        // Fields are already loaded in loadReportTemplates, so just auto-populate first field if needed
        const reportFilter = this.visibleReportFilters.find(f => f.reportTemplateId === templateId)
        if (reportFilter && reportFilter.fields.length === 1 && !reportFilter.fields[0].fieldId) {
          if (this.reportFields[templateId] && this.reportFields[templateId].length > 0) {
            reportFilter.fields[0].fieldId = this.reportFields[templateId][0].id
            reportFilter.fields[0].colorAssignments = reportFilter.fields[0].colorAssignments || {}
            this.loadFieldOptions(this.reportFields[templateId][0].id)
          }
        }
      },
      async loadFieldOptions (fieldId) {
        try {
          // Find the field in our loaded data
          let field = null
          Object.values(this.reportFields).forEach(fields => {
            const foundField = fields.find(f => f.id === fieldId)
            if (foundField) {
              field = foundField
            }
          })

          if (field) {
            // Convert field options to the format expected by our dropdowns
            let options = []

            if (field.options_i18n && typeof field.options_i18n === 'object') {
              // Use translated options if available
              const currentLocale = this.$i18n?.locale || 'en'

              // Try current locale, then en, then first available locale
              const translatedOptions = field.options_i18n[currentLocale] ||
                field.options_i18n['en'] ||
                field.options_i18n[Object.keys(field.options_i18n)[0]] ||
                {}

              if (Object.keys(translatedOptions).length > 0) {
                options = Object.keys(translatedOptions).map(key => ({
                  value: key,
                  label: translatedOptions[key]
                }))
              } else if (Array.isArray(field.options) && field.options.length > 0) {
                // Fallback to original options array
                options = field.options.map(option => ({
                  value: option,
                  label: option
                }))
              }
            } else if (Array.isArray(field.options) && field.options.length > 0) {
              // Use original options as both value and label
              options = field.options.map(option => ({
                value: option,
                label: option
              }))
            }

            this.reportFieldOptions[fieldId] = options
          } else {
            this.reportFieldOptions[fieldId] = []
          }
        } catch (error) {
          console.error('Failed to load field options:', error)
          this.reportFieldOptions[fieldId] = []
        }
      },

      // Version Management
      async toggleVersions () {
        this.showVersions = !this.showVersions
        if (this.showVersions && this.versions.length === 0) {
          await this.loadVersions()
        }
      },

      async loadVersions () {
        this.loadingVersions = true
        try {
          const response = await fetchEcosystemMapVersions(this.mapId)
          this.versions = response.versions
        } catch (error) {
          console.error('Failed to load versions:', error)
          alert('Failed to load version history')
        } finally {
          this.loadingVersions = false
        }
      },

      async restoreVersion (versionId) {
        if (!confirm('Are you sure you want to restore this version? This will create a new version with the restored data.')) {
          return
        }

        this.restoringVersion = true
        try {
          await restoreEcosystemMapVersion(this.mapId, versionId)
          alert('Version restored successfully')

          // Reload the map data
          await this.loadMapData()

          // Reload versions
          await this.loadVersions()

          // Force preview refresh
          this.refreshPreview()
        } catch (error) {
          console.error('Failed to restore version:', error)
          alert('Failed to restore version')
        } finally {
          this.restoringVersion = false
        }
      },

      async openLabelDialog (version) {
        const label = prompt('Enter a label for this version:', version.label || '')

        if (label === null) return // User cancelled

        try {
          await updateEcosystemMapVersionLabel(this.mapId, version.id, label)
          alert('Version label updated')
          await this.loadVersions()
        } catch (error) {
          console.error('Failed to update version label:', error)
          alert('Failed to update version label')
        }
      },

      formatDate (dateString) {
        const date = new Date(dateString)
        return date.toLocaleString('nl-BE', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })
      },

      async previewVersion (version) {
        this.showPreviewModal = true
        this.loadingPreview = true
        this.previewVersionId = version.id
        this.previewData = null

        try {
          const response = await fetchEcosystemMapVersion(this.mapId, version.id)
          this.previewData = response.version
          console.log('Preview data:', this.previewData)
          console.log('Report field filters:', this.previewData.report_field_filters)
        } catch (error) {
          console.error('Failed to load version details:', error)
          alert('Failed to load version details')
          this.closePreviewModal()
        } finally {
          this.loadingPreview = false
        }
      },

      closePreviewModal () {
        this.showPreviewModal = false
        this.previewData = null
        this.previewVersionId = null
      },

      async restoreVersionFromPreview () {
        if (this.previewVersionId) {
          await this.restoreVersion(this.previewVersionId)
          this.closePreviewModal()
        }
      },

      getSpottingAreaNameById (id) {
        const area = this.spottingAreas.find(sa => sa.id === id)
        return area ? area.name : 'Unknown'
      },

      hasCoordinateChanged (versionMapData) {
        // Compare coordinates with proper float parsing and rounding to avoid false positives
        const currentLat = parseFloat(this.mapConfig.lat).toFixed(6)
        const currentLng = parseFloat(this.mapConfig.lng).toFixed(6)
        const versionLat = parseFloat(versionMapData.lat).toFixed(6)
        const versionLng = parseFloat(versionMapData.lng).toFixed(6)

        return currentLat !== versionLat || currentLng !== versionLng
      },

      getReportTemplateName (templateId) {
        if (!templateId) return 'Unknown Template'
        if (!this.reportTemplates || this.reportTemplates.length === 0) return `Template #${templateId}`
        const template = this.reportTemplates.find(t => t.id === templateId)
        return template ? template.name : `Template #${templateId}`
      },

      getReportFieldName (fieldId) {
        if (!fieldId) return 'Unknown Field'
        if (!this.reportFields || Object.keys(this.reportFields).length === 0) return `Field #${fieldId}`
        // Search through all fields in reportFields
        for (const templateId in this.reportFields) {
          const field = this.reportFields[templateId].find(f => f.id === fieldId)
          if (field) {
            return field.label
          }
        }
        return `Field #${fieldId}`
      },

      hasFilterChanged (index) {
        // Simple check: if the number of filters differs or the filter doesn't exist in current
        if (index >= this.visibleReportFilters.length) {
          return true
        }
        // Deep comparison would be more complex, so we just highlight if counts differ
        return this.visibleReportFilters.length !== this.previewData.report_field_filters.length
      },

      parseColors (colorsData) {
        if (!colorsData) return {}
        if (typeof colorsData === 'string') {
          try {
            return JSON.parse(colorsData)
          } catch {
            return {}
          }
        }
        return colorsData
      },

      parseAllowedValues (allowedValues) {
        if (!allowedValues) return []

        // If it's already an array, return it
        if (Array.isArray(allowedValues)) {
          return allowedValues
        }

        // If it's a string, try to parse it
        if (typeof allowedValues === 'string') {
          try {
            const parsed = JSON.parse(allowedValues)
            // If parsed result is an object, get its values
            if (typeof parsed === 'object' && !Array.isArray(parsed)) {
              return Object.values(parsed)
            }
            // If it's an array after parsing, return it
            if (Array.isArray(parsed)) {
              return parsed
            }
            // Otherwise return the string wrapped in an array
            return [allowedValues]
          } catch {
            return [allowedValues]
          }
        }

        // If it's an object, get its values
        if (typeof allowedValues === 'object') {
          return Object.values(allowedValues)
        }

        // Fallback: wrap in array
        return [allowedValues]
      },

      getColorAssignments (allowedValues, allowedValuesColors) {
        const values = this.parseAllowedValues(allowedValues)
        const colors = this.parseColors(allowedValuesColors)

        console.log('getColorAssignments - values:', values)
        console.log('getColorAssignments - colors:', colors)
        console.log('getColorAssignments - colors type:', typeof colors, 'isArray:', Array.isArray(colors))

        // If colors is already an object with value keys, return it
        if (colors && typeof colors === 'object' && !Array.isArray(colors)) {
          const colorKeys = Object.keys(colors)
          console.log('getColorAssignments - color keys:', colorKeys)

          // Check if keys match values (it's already a proper mapping)
          const firstKey = colorKeys[0]
          if (firstKey && values.includes(firstKey)) {
            console.log('getColorAssignments - returning colors as-is (value keys match)')
            return colors
          }

          // Check if keys are numeric indices and values are objects with {value, color}
          if (colorKeys.every(k => !isNaN(k))) {
            const result = {}
            colorKeys.forEach(key => {
              const colorData = colors[key]
              // Check if it's an object with value and color properties
              if (colorData && typeof colorData === 'object' && colorData.value && colorData.color) {
                result[colorData.value] = colorData.color
              } else if (typeof colorData === 'string') {
                // It's a simple color string
                const value = values[parseInt(key)]
                if (value) {
                  result[value] = colorData
                }
              }
            })
            console.log('getColorAssignments - mapped numeric keys to values:', result)
            return result
          }
        }

        // If colors is an array, map it to values
        if (Array.isArray(colors)) {
          const result = {}
          colors.forEach((colorData, index) => {
            // Check if it's an object with value and color properties
            if (colorData && typeof colorData === 'object' && colorData.value && colorData.color) {
              result[colorData.value] = colorData.color
            } else if (typeof colorData === 'string') {
              // It's a simple color string
              const value = values[index]
              if (value) {
                result[value] = colorData
              }
            }
          })
          console.log('getColorAssignments - result from array:', result)
          return result
        }

        console.log('getColorAssignments - returning empty object')
        return {}
      },
    },
    beforeUnmount () {
      // Clean up debounce timer when component is destroyed
      if (this.refreshDebounceTimer) {
        clearTimeout(this.refreshDebounceTimer)
        this.refreshDebounceTimer = null
      }
    }
  })
</script>

<style lang="scss" scoped>
  .tw-container {
    max-width: 1200px;
  }

  code {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 12px;
    line-height: 1.4;
  }

  .membership-group {
    .filter-buttons {
      display: flex;
      gap: 0.25rem;
      border-radius: 6px;
      background: #f3f4f6;
      padding: 2px;

      .filter-btn {
        padding: 0.375rem 0.75rem;
        font-size: 0.75rem;
        font-weight: 500;
        color: #6b7280;
        background: transparent;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        transition: all 0.2s ease;
        white-space: nowrap;

        &:hover {
          color: #374151;
        }

        &.active {
          background: var(--primary-community-lightest);
          color: var(--primary-community, #3b82f6);
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }
      }
    }
  }
</style>
